import {
  Box,
  useTheme,
  Autocomplete,
  TextField,
  Checkbox,
  FormControlLabel,
  Typography,
  Button,
  IconButton,
  MenuItem,
  createFilterOptions,
  InputAdornment,
  Switch,
  Paper,
  useMediaQuery,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
} from "@mui/material";
import dayjs from "dayjs";
import durationPlugin from "dayjs/plugin/duration";
import { getRequestorInvoiceDefaults } from "helpers/requestorGroupHelpers";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";
import { useStateContext } from "contexts/ContextProvider";
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import Overlay from "components/Overlay";
import Grid from "@mui/material/Grid2";
import api from "services/api";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import { OVERLAY_NAMES } from "constants/Overlays";
import utc from "dayjs/plugin/utc";
import { HtmlTooltip } from "components/HeatMap";
import { CustomPopper } from "components/CustomPopper";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlankRounded";
import CheckBoxIcon from "@mui/icons-material/CheckBoxRounded";
import { useAuth } from "../../../contexts/AuthContext";
import { getBorderColor } from "../interpretations/InterpretationNewEdit";
import { usePageTitle } from "hooks/usePageTitle";
import { useEditLock } from "hooks/useEditLock";
import EditLockDialog from "components/EditLockDialog";
import { useWebSocket } from "contexts/WebSocketContext";
import { usePresence } from "contexts/PresenceContext";
import useAssignmentUpdates from "hooks/useAssignmentUpdates";
import { useNavigate } from "react-router-dom";

const EMPTY_LINGUIST_CLIENT_STATS = {
  completedAssignments: 0,
  cancelledUnableToFillAssignments: 0,
};

const EMPTY_LANGUAGE_CLIENT_STATS = {
  requestCount: 0,
};

const LONG_PRESS_AUTOFILL_MS = 900;
const LOREM_NAME_SEEDS = [
  "Lorem ipsum",
  "Dolor sit",
  "Amet consectetur",
  "Adipiscing elit",
  "Sed do",
  "Eiusmod tempor",
  "Incididunt ut",
  "Labore magna",
];

const OtherServiceNewEdit = () => {
  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  const { currentUser } = useAuth();
  const { socket } = useWebSocket();
  const { currentEntity, setCurrentEntity } = usePresence();
  const isMobile = !useMediaQuery("(min-width:600px)");

  const navigate = useNavigate();
  
  const filter = createFilterOptions();

  const theme = useTheme();
  const optionSwitchSx = {
    ml: 0.5,
    mr: 0.25,
    "& .MuiSwitch-track": {
      backgroundColor: theme.palette.common.white,
      opacity: "1 !important",
      border: `1px solid ${theme.palette.grey[300]}`,
    },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
      backgroundColor: `${theme.palette.primary.main}40 !important`,
      borderColor: "transparent",
      opacity: "1 !important",
    },
    "& .MuiSwitch-switchBase:not(.Mui-checked) .MuiSwitch-thumb": {
      backgroundColor: theme.palette.grey[400],
    },
  };

  const {
    overlayData,
    setOverlayData,
    setOverlayName,
    triggerPageRefresh,
    showSnackbar,
    openDialog,
    closeDialog,
  } = useStateContext();

  usePageTitle(
    overlayData.Action === OVERLAY_NAMES.OTHERSERVICE_ACTION_NEW
      ? "New Other Service"
      : "Edit Other Service",
  );

  const [isSavingChanges, setIsSavingChanges] = useState(false);
  const [isEditable, setIsEditable] = useState(true);

  // State for paid assignment warning dialog
  const [showPaidWarning, setShowPaidWarning] = useState(false);
  const [paidWarningMessage, setPaidWarningMessage] = useState("");
  const [pendingSave, setPendingSave] = useState(false);

  const isNewAssignment =
    overlayData.Action === OVERLAY_NAMES.OTHERSERVICE_ACTION_NEW;
  const prefilledData = useMemo(
    () => (isNewAssignment ? overlayData?.PrefilledData || {} : {}),
    [isNewAssignment, overlayData?.PrefilledData],
  );
  const isAmineUser = (currentUser?.Name || "").trim() === "Amine";

  // Paid-invoice lock: a service on a PAID invoice is frozen — Save is
  // disabled and a red message shows instead of the confirm-dialog dance.
  const paidLocked =
    !isNewAssignment && Boolean(overlayData?.Invoice?.PaymentReceivedDate);

  // Freeze ALL fields too (not just Save): force isEditable off while the
  // service sits on a paid invoice. Runs whenever either changes, so no
  // error-recovery setIsEditable(true) can re-enable a paid-locked form.
  useEffect(() => {
    if (paidLocked) setIsEditable(false);
  }, [paidLocked, isEditable]);

  // Edit lock for existing services only
  const { isLocked, lockedBy, releaseLock } = useEditLock(
    "service",
    isNewAssignment ? null : overlayData.id,
  );

  // Snapshot the entity the underlying view (list/profile) had before this
  // overlay took over, so closing the overlay can restore it instead of
  // nulling it — nulling would drop the still-mounted list/profile out of its
  // entity room and it would stop receiving live updates.
  const previousEntityRef = useRef(null);
  const hasTakenOverEntityRef = useRef(false);

  // Set entity tracking for presence and updates
  useEffect(() => {
    if (!isNewAssignment && overlayData.id) {
      if (!hasTakenOverEntityRef.current) {
        hasTakenOverEntityRef.current = true;
        // Snapshot only on first takeover: currentEntity is intentionally NOT
        // a dependency, so the closure keeps the underlying view's entity from
        // the render that created this effect.
        previousEntityRef.current = currentEntity;
      }
      setCurrentEntity({
        entityType: "OtherService",
        entityId: overlayData.id,
      });
    }
    return () => {
      if (hasTakenOverEntityRef.current) {
        // Restore the underlying view's entity (keeps its room alive) instead
        // of clearing it; fall back to clearing when there was nothing to
        // restore.
        setCurrentEntity(
          previousEntityRef.current || { entityType: null, entityId: null },
        );
        hasTakenOverEntityRef.current = false;
        previousEntityRef.current = null;
      }
    };
  }, [isNewAssignment, overlayData.id, setCurrentEntity]);

  // Tracks whether the user has unsaved form edits. Kept in a ref (declared
  // before the state it mirrors, since `hasChanges` is defined later in the
  // component body) so the socket refetch handler can check it synchronously.
  const hasChangesRef = useRef(false);

  // Listen for assignment updates from other members
  const handleAssignmentRefetch = () => {
    // If the user has unsaved edits, don't clobber their form with the remote
    // snapshot. Refreshing overlayData here would reset the dirty baseline
    // (initialDataRef syncs from overlayData) and silently discard their work.
    // Keep the list/profile behind the overlay fresh and warn instead.
    if (hasChangesRef.current) {
      triggerPageRefresh();
      showSnackbar(
        "This assignment was updated by another member. Review your changes before saving.",
        theme,
        "info",
        theme.palette.warning.main,
      );
      return;
    }
    // Reload the overlay data with fresh data from server
    api
      .get(`/services/otherServices/${overlayData.id}`)
      .then((response) => {
        setOverlayData({
          ...overlayData,
          ...response.data,
        });
        // Refresh the list behind the overlay, but skip the success snackbar —
        // form fields are local state and don't change on remote updates, so
        // "Assignment data refreshed" would be misleading.
        triggerPageRefresh();
      })
      .catch((err) => console.log("Error refetching assignment:", err));
  };

  useAssignmentUpdates({
    modelType: "OtherService",
    recordId: isNewAssignment ? null : overlayData.id,
    onUpdate: handleAssignmentRefetch,
  });

  const [, setAddNewClient] = useState(false);
  const [, setAddNewRequestor] = useState(false);
  const [, setAddNewInvoiceTo] = useState(false);

  const completing =
    overlayData.Action === OVERLAY_NAMES.OTHERSERVICE_ACTION_COMPLETE;

  const [name, setName] = useState(
    isNewAssignment ? prefilledData.Name || "" : overlayData.Name,
  );

  const [date, setDate] = useState(
    isNewAssignment
      ? prefilledData.Date
        ? dayjs(prefilledData.Date)
        : dayjs()
      : dayjs(overlayData.Date),
  );

  const [deadline, setDeadline] = useState(
    isNewAssignment
      ? prefilledData.Deadline
        ? dayjs(prefilledData.Deadline)
        : null
      : dayjs(overlayData.Deadline),
  );

  const [deliveryDate, setDeliveryDate] = useState(
    isNewAssignment
      ? prefilledData.DeliveryDate
        ? dayjs(prefilledData.DeliveryDate)
        : null
      : dayjs(overlayData.DeliveryDate),
  );

  const [projectNumber, setProjectNumber] = useState(
    overlayData.ProjectNumber || "",
  );

  useEffect(() => {
    if (!isNewAssignment) return;

    let isMounted = true;

    api
      .get("/services/otherServices/latestProjectNumber")
      .then((response) => {
        if (!isMounted) return;
        const nextProjectNumber =
          Number(response?.data?.ProjectNumber || 0) + 1;
        setProjectNumber(String(nextProjectNumber));
      })
      .catch((error) => {
        console.error(
          "[OtherServiceNewEdit] Error fetching latest project number:",
          error,
        );
      });

    return () => {
      isMounted = false;
    };
  }, [isNewAssignment]);

  const [otherServiceType, setOtherServiceType] = useState(
    isNewAssignment
      ? prefilledData.OtherServiceType || ""
      : overlayData.ServiceSubtype?.Name,
  );
  const [otherSubtypeOptions, setOtherSubtypeOptions] = useState([]);

  const resolveOtherSubtypeId = useCallback(
    (typeName) => {
      const normalized = String(typeName || "").trim().toLowerCase();
      if (!normalized) return null;
      const match = otherSubtypeOptions.find(
        (opt) => String(opt?.Name || "").trim().toLowerCase() === normalized,
      );
      const parsed = Number(match?.id);
      return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
    },
    [otherSubtypeOptions],
  );

  const [client, setClient] = useState(
    isNewAssignment ? prefilledData.Client || "" : overlayData.Client,
  );
  const [languageClientStatsById, setLanguageClientStatsById] = useState({});

  const [requestor, setRequestor] = useState(
    isNewAssignment ? prefilledData.Requestor || "" : overlayData.Requestor,
  );

  const [invoiceTo, setInvoiceTo] = useState(
    isNewAssignment ? prefilledData.Invoicer || "" : overlayData.Invoicer,
  );

  // CC recipients resolved from the requestor's group (read-only display;
  // not persisted on the service).
  const [ccRequestors, setCcRequestors] = useState([]);
  const applyRequestorInvoiceDefaults = useCallback((requestorValue) => {
    const { invoiceTo: nextInvoiceTo, ccRequestors: nextCcRequestors } =
      getRequestorInvoiceDefaults(requestorValue);
    setInvoiceTo(nextInvoiceTo || "");
    setCcRequestors(nextCcRequestors);
  }, []);

  const assignmentTargetLanguages =
    overlayData.ServiceTargetLanguages &&
    overlayData.ServiceTargetLanguages.map((language) => ({
      id: language.LanguageID,
      Name: language?.Language?.Name,
    }));

  const [targetLanguages, setTargetLanguages] = useState(
    isNewAssignment
      ? prefilledData.TargetLanguages || []
      : assignmentTargetLanguages || [],
  );

  const [unitTypeId, setUnitTypeId] = useState(
    isNewAssignment
      ? prefilledData.UnitTypeID || ""
      : overlayData.UnitTypeID || overlayData.UnitType?.id || "",
  );

  const [unitCount, setUnitCount] = useState(
    isNewAssignment
      ? (prefilledData.UnitCount ?? prefilledData.WordCount ?? "")
      : (overlayData.UnitCount ?? overlayData.WordCount ?? 0),
  );

  const [unitRate, setUnitRate] = useState(
    isNewAssignment
      ? (prefilledData.UnitRate ?? "")
      : (overlayData.UnitRate ?? ""),
  );

  const [unitTypes, setUnitTypes] = useState([]);

  const [wordCount] = useState(
    isNewAssignment ? 0 : overlayData.WordCount || 0,
  );

  const [numberOfLanguages, setNumberOfLanguages] = useState(
    isNewAssignment
      ? prefilledData.NumberOfLanguages || prefilledData.TargetLanguages?.length || 1
      : overlayData.NumberOfLanguages || targetLanguages.length,
  );

  const [deliveryType, setDeliveryType] = useState(
    isNewAssignment ? prefilledData.DeliveryType || "" : overlayData.DeliveryType,
  );

  const [paymentType, setPaymentType] = useState(
    isNewAssignment ? prefilledData.PaymentType || "" : overlayData.PaymentType,
  );

  const [paymentFees, setPaymentFees] = useState(
    isNewAssignment ? prefilledData.PaymentFees || "" : overlayData.PaymentFees,
  );

  const [deliveryCost, setDeliveryCost] = useState(
    isNewAssignment ? prefilledData.DeliveryCost || "" : overlayData.DeliveryCost,
  );

  const [linguist, setLinguist] = useState(
    isNewAssignment ? prefilledData.Linguist || null : overlayData.Linguist || 0,
  );
  const [linguistClientStatsById, setLinguistClientStatsById] = useState({});
  const hasSelectedClient = Number(client?.id || 0) > 0;

  const languageOptions = useMemo(() => {
    // "ALL" is the rate pseudo-language (used by client default rates) — it
    // must never appear in service language pickers.
    const languages = [...(overlayData.Languages || [])].filter(
      (language) =>
        String(language?.Name || "").trim().toUpperCase() !== "ALL",
    );

    if (!hasSelectedClient) {
      return languages;
    }

    return languages.sort((left, right) => {
      const requestDelta =
        (languageClientStatsById[Number(right?.id || 0)]?.requestCount || 0) -
        (languageClientStatsById[Number(left?.id || 0)]?.requestCount || 0);

      if (requestDelta !== 0) return requestDelta;
      return String(left?.Name || "").localeCompare(String(right?.Name || ""));
    });
  }, [hasSelectedClient, languageClientStatsById, overlayData.Languages]);

  const linguistOptions = useMemo(
    () =>
      [...(overlayData.Linguists || [])]
        .map((linguistOption) => ({
          ...linguistOption,
          ClientServiceStats:
            linguistClientStatsById[Number(linguistOption?.id || 0)] ||
            EMPTY_LINGUIST_CLIENT_STATS,
        }))
        .sort((left, right) => {
          const completedDelta =
            (right?.ClientServiceStats?.completedAssignments || 0) -
            (left?.ClientServiceStats?.completedAssignments || 0);

          if (completedDelta !== 0) return completedDelta;
          return String(left?.Name || "").localeCompare(String(right?.Name || ""));
        }),
    [overlayData.Linguists, linguistClientStatsById],
  );

  const renderLinguistOption = useCallback((props, option) => {
    if (option?.inputValue || !option?.id) {
      return (
        <Box component="li" {...props}>
          {option?.Name || ""}
        </Box>
      );
    }

    const stats = option.ClientServiceStats || EMPTY_LINGUIST_CLIENT_STATS;
    const statsLabel =
      stats.cancelledUnableToFillAssignments > 0
        ? `Completed: ${stats.completedAssignments} | Unable to fill: ${stats.cancelledUnableToFillAssignments}`
        : `Completed: ${stats.completedAssignments}`;

    return (
      <Box
        component="li"
        {...props}
        sx={{ display: "flex", alignItems: "center", width: "100%", gap: 1 }}
      >
        <Box component="span" sx={{ minWidth: 0 }}>
          {option?.Name || ""}
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ ml: "auto", textAlign: "right", whiteSpace: "nowrap" }}
        >
          {statsLabel}
        </Typography>
      </Box>
    );
  }, []);

  useEffect(() => {
    let isCancelled = false;

    const clientId = Number(client?.id || 0);
    const linguistIds = (overlayData.Linguists || [])
      .map((linguistOption) => Number(linguistOption?.id || 0))
      .filter((id) => id > 0);

    if (!clientId || !linguistIds.length) {
      setLinguistClientStatsById({});
      return undefined;
    }

    const fetchLinguistClientStats = async () => {
      try {
        const response = await api.post("/linguists/client-service-stats", {
          clientId,
          linguistIds,
        });

        if (!isCancelled) {
          setLinguistClientStatsById(response.data || {});
        }
      } catch (error) {
        console.error("Failed to fetch linguist client service stats:", error);
        if (!isCancelled) {
          setLinguistClientStatsById({});
        }
      }
    };

    fetchLinguistClientStats();

    return () => {
      isCancelled = true;
    };
  }, [client, overlayData.Linguists]);

  useEffect(() => {
    let isCancelled = false;

    const clientId = Number(client?.id || 0);
    const languageIds = (overlayData.Languages || [])
      .map((languageOption) => Number(languageOption?.id || 0))
      .filter((id) => id > 0);

    if (!clientId || !languageIds.length) {
      setLanguageClientStatsById({});
      return undefined;
    }

    const fetchLanguageClientStats = async () => {
      try {
        const response = await api.post("/languages/client-service-request-stats", {
          clientId,
          languageIds,
          serviceCategory: "Other",
        });

        if (!isCancelled) {
          setLanguageClientStatsById(response.data || {});
        }
      } catch (error) {
        console.error("Failed to fetch language client service stats:", error);
        if (!isCancelled) {
          setLanguageClientStatsById({});
        }
      }
    };

    fetchLanguageClientStats();

    return () => {
      isCancelled = true;
    };
  }, [client, overlayData.Languages]);

  const [otherServiceStatus, setOtherServiceStatus] = useState(
    isNewAssignment
      ? prefilledData.OtherServiceStatus || "In-Progress"
      : completing
        ? "Completed"
        : overlayData.OtherServiceStatus,
  );

  useEffect(() => {
    let isMounted = true;

    api
      .get("/services/otherServices/types/options")
      .then((response) => {
        if (!isMounted) return;
        setOtherSubtypeOptions(Array.isArray(response.data) ? response.data : []);
      })
      .catch((error) => {
        console.error("Error fetching other service subtype options:", error);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const [accountCharge, setAccountCharge] = useState(
    isNewAssignment ? prefilledData.AccountCharge || "" : overlayData.AccountCharge,
  );

  const [totalCost, setTotalCost] = useState(
    isNewAssignment ? prefilledData.TotalCost || "" : overlayData.TotalCost,
  );

  const [totalLinguistPay, setTotalLinguistPay] = useState(
    isNewAssignment
      ? prefilledData.TotalLinguistPay || prefilledData.TotalCost || ""
      : (overlayData.TotalLinguistPay ?? overlayData.TotalCost ?? ""),
  );

  const [totalCostHelperText, setTotalCostHelperText] = useState("");
  const [accountChargeHelperText, setAccountChargeHelperText] = useState("");

  const [linguistPay, setLinguistPay] = useState(
    isNewAssignment ? Number(prefilledData.LinguistPay || 0) : overlayData.LinguistPay,
  );

  const [additionalCost, setAdditionalCost] = useState(
    isNewAssignment ? Number(prefilledData.AdditionalCost || 0) : overlayData.AdditionalCost,
  );

  const [additionalLinguistCost, setAdditionalLinguistCost] = useState(
    isNewAssignment
      ? prefilledData.AdditionalLinguistCost || ""
      : overlayData.AdditionalLinguistCost,
  );

  const [rate, setRate] = useState(
    isNewAssignment ? Number(prefilledData.Rate || 0) : overlayData.Rate || 0,
  );

  const [profit, setProfit] = useState(
    isNewAssignment ? prefilledData.Profit || "" : overlayData.Profit,
  );

  const [margin, setMargin] = useState(
    isNewAssignment ? prefilledData.Margin || "" : overlayData.Margin,
  );

  const [details, setDetails] = useState(
    isNewAssignment ? prefilledData.Details || "" : overlayData.Details,
  );

  const [allowDetailsEditing, setAllowDetailsEditing] = useState(false);
  const [showLinguistPayAndOtherCosts, setShowLinguistPayAndOtherCosts] =
    useState(completing ? true : false);
  const [costRows, setCostRows] = useState([]);
  // Amounts for custom DB cost types (any type not in the built-in list),
  // keyed by typeKey so get/setCostValueByType work for ALL cost types.
  const [customCostValuesByType, setCustomCostValuesByType] = useState({});
  const [nextCostRowId, setNextCostRowId] = useState(1);

  const [nameError, setNameError] = useState(false);
  const [dateError, setDateError] = useState(false);
  const [projectNumberError, setProjectNumberError] = useState(false);
  const [projectNumberExists, setProjectNumberExists] = useState(false);
  const [otherServiceTypeError, setOtherServiceTypeError] = useState(false);
  const [clientError, setClientError] = useState(false);
  const [requestorError, setRequestorError] = useState(false);
  const [invoiceToError, setInvoiceToError] = useState(false);
  const [linguistError, setLinguistError] = useState(false);
  const [otherServiceStatusError, setOtherServiceStatusError] = useState(false);
  const [accountChargeError, setAccountChargeError] = useState(false);
  const [totalCostError, setTotalCostError] = useState(false);
  const [linguistPayError, setLinguistPayError] = useState(false);
  const [, setAdditionalCostError] = useState(false);
  const [, setAdditionalLinguistCostError] =
    useState(false);
  const [rateError, setRateError] = useState(false);
  const [, setDeadlineError] = useState(false);
  const [deliveryDateError, setDeliveryDateError] = useState(false);
  const [wordCountError, setWordCountError] = useState(false);
  const [deliveryTypeError, setDeliveryTypeError] = useState(false);
  const [paymentTypeError, setPaymentTypeError] = useState(false);
  const [, setPaymentFeesError] = useState(false);
  const [, setDeliveryCostError] = useState(false);
  const [detailsError, setDetailsError] = useState(false);

  const [shouldValidate, setShouldValidate] = useState(false);

  const baseCostTypeOptions = useMemo(
    () => [
      {
        key: "linguistPay",
        label: "Linguist Pay",
        costTypeName: "Linguist Pay",
        sourceLegacyField: "LinguistPay",
        defaultIncludeInTotalCost: true,
        defaultIncludeInAccountCharge: false,
        includeInLinguistPay: false,
      },
      {
        key: "additionalCost",
        label: "Additional Costs",
        costTypeName: "Additional Cost",
        sourceLegacyField: "AdditionalCost",
        defaultIncludeInTotalCost: true,
        defaultIncludeInAccountCharge: true,
        includeInLinguistPay: false,
      },
      {
        key: "additionalLinguistCost",
        label: "Additional Linguist Cost",
        costTypeName: "Additional Linguist Cost",
        sourceLegacyField: "AdditionalLinguistCost",
        defaultIncludeInTotalCost: true,
        defaultIncludeInAccountCharge: true,
        includeInLinguistPay: true,
      },
      {
        key: "deliveryCost",
        label: "Delivery Cost",
        costTypeName: "Delivery Cost",
        sourceLegacyField: "DeliveryCost",
        defaultIncludeInTotalCost: true,
        defaultIncludeInAccountCharge: true,
        includeInLinguistPay: true,
      },
      {
        key: "paymentFees",
        label: "Payment Fees",
        costTypeName: "Payment Fees",
        sourceLegacyField: "PaymentFees",
        defaultIncludeInTotalCost: true,
        defaultIncludeInAccountCharge: false,
        includeInLinguistPay: true,
      },
    ],
    [],
  );
  const [costTypeRulesByName, setCostTypeRulesByName] = useState({});

  useEffect(() => {
    let isMounted = true;

    const loadCostTypeRules = async () => {
      try {
        const response = await api.get("/serviceCosts/types/by-category/Other");
        const rows = Array.isArray(response.data) ? response.data : [];

        if (!isMounted) return;

        const byRuleKey = rows.reduce((acc, row) => {
          const normalizedName = String(row?.Name || "").trim().toLowerCase();
          const normalizedSource = String(row?.SourceLegacyField || "").trim().toLowerCase();
          const rule = {
            isEnabled: Boolean(row?.IsEnabledForCategory),
            includeInTotalCost: Boolean(row?.EffectiveIncludeInTotalCost),
            includeInAccountCharge: Boolean(row?.EffectiveIncludeInAccountCharge),
            includeInLinguistPay:
              row?.EffectiveIncludeInLinguistPay != null
                ? Boolean(row.EffectiveIncludeInLinguistPay)
                : null,
          };

          if (normalizedName) {
            acc[`name:${normalizedName}`] = rule;
          }
          if (normalizedSource) {
            acc[`source:${normalizedSource}`] = rule;
          }
          return acc;
        }, {});

        setCostTypeRulesByName(byRuleKey);
      } catch (error) {
        console.error("Error loading other service cost type rules:", error);
      }
    };

    loadCostTypeRules();

    return () => {
      isMounted = false;
    };
  }, []);

  const costTypeOptions = useMemo(
    () =>
      baseCostTypeOptions.map((option) => {
        const rule =
          costTypeRulesByName[
            `source:${String(option.sourceLegacyField || "").toLowerCase()}`
          ] ||
          costTypeRulesByName[
            `name:${String(option.costTypeName || "").toLowerCase()}`
          ] ||
          null;

        return {
          ...option,
          isEnabled: rule ? rule.isEnabled : true,
          effectiveIncludeInTotalCost: rule
            ? rule.includeInTotalCost
            : option.defaultIncludeInTotalCost,
          effectiveIncludeInAccountCharge: rule
            ? rule.includeInAccountCharge
            : option.defaultIncludeInAccountCharge,
          effectiveIncludeInLinguistPay:
            rule && rule.includeInLinguistPay != null
              ? rule.includeInLinguistPay
              : option.includeInLinguistPay,
        };
      }),
    [baseCostTypeOptions, costTypeRulesByName],
  );

  // Per-row inclusion flags: stored line flags win; fall back to the cost
  // type's effective defaults for pre-migration lines / new rows.
  // Payment Fees are ALWAYS excluded from Account Charge (Amine decision).
  const getRowInclusionFlags = useCallback(
    (typeKey, line) => {
      const option = costTypeOptions.find((opt) => opt.key === typeKey);
      const includeInTotalCost =
        line?.IncludeInTotalCost != null
          ? Boolean(line.IncludeInTotalCost)
          : Boolean(option?.effectiveIncludeInTotalCost ?? true);
      const includeInLinguistPay =
        line?.IncludeInLinguistPay != null
          ? Boolean(line.IncludeInLinguistPay)
          : Boolean(
              option?.effectiveIncludeInLinguistPay ??
                option?.includeInLinguistPay ??
                false,
            );
      const includeInAccountCharge =
        typeKey === "paymentFees"
          ? false
          : line?.IncludeInAccountCharge != null
            ? Boolean(line.IncludeInAccountCharge)
            : Boolean(option?.effectiveIncludeInAccountCharge ?? false);
      return { includeInTotalCost, includeInLinguistPay, includeInAccountCharge };
    },
    [costTypeOptions],
  );

  const sourceFieldToCostTypeKey = useMemo(
    () => ({
      LinguistPay: "linguistPay",
      AdditionalCost: "additionalCost",
      AdditionalLinguistCost: "additionalLinguistCost",
      DeliveryCost: "deliveryCost",
      PaymentFees: "paymentFees",
    }),
    [],
  );

  const costTypeNameToCostTypeKey = useMemo(
    () => ({
      "linguist pay": "linguistPay",
      "additional cost": "additionalCost",
      "additional costs": "additionalCost",
      "additional linguist cost": "additionalLinguistCost",
      "delivery cost": "deliveryCost",
      "payment fees": "paymentFees",
      "payment fee": "paymentFees",
    }),
    [],
  );

  const getCostTypeKeyFromCostLine = useCallback((line) => {
    if (!line) return "";

    const bySourceField = sourceFieldToCostTypeKey[line.SourceLegacyField];
    if (bySourceField) return bySourceField;

    const normalizedName = String(line.CostTypeName || "")
      .trim()
      .toLowerCase();
    return costTypeNameToCostTypeKey[normalizedName] || "";
  }, [sourceFieldToCostTypeKey, costTypeNameToCostTypeKey]);

  const getCostValueByType = (typeKey) => {
    switch (typeKey) {
      case "linguistPay":
        return linguistPay;
      case "additionalLinguistCost":
        return additionalLinguistCost;
      case "additionalCost":
        return additionalCost;
      case "deliveryCost":
        return deliveryCost;
      case "paymentFees":
        return paymentFees;
      default:
        return customCostValuesByType[typeKey] ?? "";
    }
  };

  const getOriginalCostValueByType = (typeKey) => {
    switch (typeKey) {
      case "linguistPay":
        return overlayData.LinguistPay;
      case "additionalLinguistCost":
        return overlayData.AdditionalLinguistCost;
      case "additionalCost":
        return overlayData.AdditionalCost;
      case "deliveryCost":
        return overlayData.DeliveryCost;
      case "paymentFees":
        return overlayData.PaymentFees;
      default:
        return "";
    }
  };

  const getOriginalCostTypeByRowId = (rowId) => {
    const initialTypeKeys = [
      Number(overlayData.LinguistPay || 0) !== 0 ? "linguistPay" : null,
      Number(overlayData.AdditionalCost || 0) !== 0 ? "additionalCost" : null,
      Number(overlayData.AdditionalLinguistCost || 0) !== 0 ? "additionalLinguistCost" : null,
      Number(overlayData.DeliveryCost || 0) !== 0 ? "deliveryCost" : null,
      Number(overlayData.PaymentFees || 0) !== 0 ? "paymentFees" : null,
    ].filter(Boolean);

    return initialTypeKeys[rowId - 1] || "";
  };

  const setCostValueByType = (typeKey, value) => {
    switch (typeKey) {
      case "linguistPay":
        setLinguistPay(value);
        setLinguistPayError(value === null);
        break;
      case "additionalLinguistCost":
        setAdditionalLinguistCost(value);
        setAdditionalLinguistCostError(value === null);
        break;
      case "additionalCost":
        setAdditionalCost(value);
        setAdditionalCostError(value === null);
        break;
      case "deliveryCost":
        setDeliveryCost(value);
        setDeliveryCostError(value === null);
        break;
      case "paymentFees":
        setPaymentFees(value);
        setPaymentFeesError(value === null);
        break;
      default:
        setCustomCostValuesByType((prev) => ({
          ...prev,
          [typeKey]: value,
        }));
        break;
    }
  };

  useEffect(() => {
    let isMounted = true;

    const initializeCostRows = async () => {
      if (isNewAssignment) {
        if (!isMounted) return;

        const prefilledCostLines = Array.isArray(prefilledData?.CostLines)
          ? prefilledData.CostLines
          : [];

        const amountsFromCostLines = prefilledCostLines.reduce((acc, line) => {
          const typeKey = getCostTypeKeyFromCostLine(line);
          if (!typeKey) return acc;

          const lineAmount = Number(line?.Amount || 0);
          if (!Number.isFinite(lineAmount)) return acc;

          acc[typeKey] = Number(
            (Number(acc[typeKey] || 0) + lineAmount).toFixed(2),
          );
          return acc;
        }, {});

        const effectiveAmountByType = {
          linguistPay:
            amountsFromCostLines.linguistPay ?? Number(prefilledData.LinguistPay || 0),
          additionalCost:
            amountsFromCostLines.additionalCost ?? Number(prefilledData.AdditionalCost || 0),
          additionalLinguistCost:
            amountsFromCostLines.additionalLinguistCost ?? Number(prefilledData.AdditionalLinguistCost || 0),
          deliveryCost:
            amountsFromCostLines.deliveryCost ?? Number(prefilledData.DeliveryCost || 0),
          paymentFees:
            amountsFromCostLines.paymentFees ?? Number(prefilledData.PaymentFees || 0),
        };

        const typeKeysFromEffectiveValues = Object.entries(effectiveAmountByType)
          .filter(([, amount]) => Number(amount || 0) !== 0)
          .map(([typeKey]) => typeKey);

        const orderedInitialTypeKeys = costTypeOptions
          .map((option) => option.key)
          .filter((key) => typeKeysFromEffectiveValues.includes(key));

        const lineByTypeKey = {};
        (Array.isArray(prefilledCostLines) ? prefilledCostLines : []).forEach(
          (line) => {
            const key = getCostTypeKeyFromCostLine(line);
            if (key && !lineByTypeKey[key]) lineByTypeKey[key] = line;
          },
        );

        const rows = orderedInitialTypeKeys.map((typeKey, index) => {
          const flags = getRowInclusionFlags(typeKey, lineByTypeKey[typeKey]);
          return {
            id: index + 1,
            typeKey,
            ...flags,
            originalIncludeInTotalCost: flags.includeInTotalCost,
            originalIncludeInLinguistPay: flags.includeInLinguistPay,
            originalIncludeInAccountCharge: flags.includeInAccountCharge,
          };
        });

        setLinguistPay(effectiveAmountByType.linguistPay);
        setAdditionalCost(effectiveAmountByType.additionalCost);
        setAdditionalLinguistCost(effectiveAmountByType.additionalLinguistCost);
        setDeliveryCost(effectiveAmountByType.deliveryCost);
        setPaymentFees(effectiveAmountByType.paymentFees);
        setCostRows(rows);
        setNextCostRowId(rows.length + 1);
        return;
      }

      let fetchedCostLines = [];
      if (overlayData?.id) {
        try {
          const response = await api.get(
            `/serviceCosts/service/${overlayData.id}/lines`,
          );
          fetchedCostLines = Array.isArray(response.data?.lines)
            ? response.data.lines
            : [];
        } catch (error) {
          console.error("Error loading service cost lines:", error);
        }
      }

      const mergedCostLines =
        fetchedCostLines.length > 0
          ? fetchedCostLines
          : Array.isArray(overlayData?.CostLines)
            ? overlayData.CostLines
            : [];

      const amountsFromCostLines = mergedCostLines.reduce((acc, line) => {
        const typeKey = getCostTypeKeyFromCostLine(line);
        if (!typeKey) return acc;

        const lineAmount = Number(line?.Amount || 0);
        if (!Number.isFinite(lineAmount)) return acc;

        acc[typeKey] = Number(
          (Number(acc[typeKey] || 0) + lineAmount).toFixed(2),
        );
        return acc;
      }, {});

      const effectiveAmountByType = {
        linguistPay:
          amountsFromCostLines.linguistPay ?? Number(overlayData.LinguistPay || 0),
        additionalCost:
          amountsFromCostLines.additionalCost ?? Number(overlayData.AdditionalCost || 0),
        additionalLinguistCost:
          amountsFromCostLines.additionalLinguistCost ??
          Number(overlayData.AdditionalLinguistCost || 0),
        deliveryCost:
          amountsFromCostLines.deliveryCost ?? Number(overlayData.DeliveryCost || 0),
        paymentFees:
          amountsFromCostLines.paymentFees ?? Number(overlayData.PaymentFees || 0),
      };

      const totalLinguistPayFallback = Number(overlayData.TotalLinguistPay || 0);
      const hasAnyExplicitLinguistCostComponent =
        Number(effectiveAmountByType.linguistPay || 0) !== 0 ||
        Number(effectiveAmountByType.additionalLinguistCost || 0) !== 0 ||
        Number(effectiveAmountByType.deliveryCost || 0) !== 0;

      // Some legacy rows only persisted TotalLinguistPay; show it as Linguist Pay when components are absent.
      if (
        !hasAnyExplicitLinguistCostComponent &&
        totalLinguistPayFallback !== 0
      ) {
        effectiveAmountByType.linguistPay = Number(
          totalLinguistPayFallback.toFixed(2),
        );
      }

      const typeKeysFromEffectiveValues = Object.entries(effectiveAmountByType)
        .filter(([, amount]) => Number(amount || 0) !== 0)
        .map(([typeKey]) => typeKey);

      const orderedInitialTypeKeys = costTypeOptions
        .map((option) => option.key)
        .filter((key) => typeKeysFromEffectiveValues.includes(key));

      const lineByTypeKey = {};
      (Array.isArray(mergedCostLines) ? mergedCostLines : []).forEach(
        (line) => {
          const key = getCostTypeKeyFromCostLine(line);
          if (key && !lineByTypeKey[key]) lineByTypeKey[key] = line;
        },
      );

      const rows = orderedInitialTypeKeys.map((typeKey, index) => {
        const flags = getRowInclusionFlags(typeKey, lineByTypeKey[typeKey]);
        return {
          id: index + 1,
          typeKey,
          ...flags,
          originalIncludeInTotalCost: flags.includeInTotalCost,
          originalIncludeInLinguistPay: flags.includeInLinguistPay,
          originalIncludeInAccountCharge: flags.includeInAccountCharge,
        };
      });

      if (!isMounted) return;

      setLinguistPay(effectiveAmountByType.linguistPay);
      setAdditionalCost(effectiveAmountByType.additionalCost);
      setAdditionalLinguistCost(effectiveAmountByType.additionalLinguistCost);
      setDeliveryCost(effectiveAmountByType.deliveryCost);
      setPaymentFees(effectiveAmountByType.paymentFees);
      setCostRows(rows);
      setNextCostRowId(rows.length + 1);
    };

    initializeCostRows();

    return () => {
      isMounted = false;
    };
  }, [
    isNewAssignment,
    prefilledData,
    overlayData,
    overlayData?.id,
    costTypeOptions,
    getCostTypeKeyFromCostLine,
    getRowInclusionFlags,
  ]);

  const addCostRow = () => {
    const usedKeys = new Set(costRows.map((row) => row.typeKey));
    const nextOption = costTypeOptions.find(
      (option) => option.isEnabled && !usedKeys.has(option.key),
    );
    if (!nextOption) return;

    setCostRows((prev) => [
      ...prev,
      {
        id: nextCostRowId,
        typeKey: nextOption.key,
        ...getRowInclusionFlags(nextOption.key),
      },
    ]);
    setNextCostRowId((prev) => prev + 1);
  };

  const removeCostRow = (rowId) => {
    const row = costRows.find((item) => item.id === rowId);
    if (row?.typeKey) {
      setCostValueByType(row.typeKey, "");
    }
    setCostRows((prev) => prev.filter((item) => item.id !== rowId));
  };

  const updateCostRowType = (rowId, nextTypeKey) => {
    const row = costRows.find((item) => item.id === rowId);
    if (row?.typeKey && row.typeKey !== nextTypeKey) {
      setCostValueByType(row.typeKey, "");
    }

    setCostRows((prev) =>
      prev.map((item) =>
        item.id === rowId
          ? {
              ...item,
              typeKey: nextTypeKey,
              ...getRowInclusionFlags(nextTypeKey),
            }
          : item,
      ),
    );
  };

  const updateCostRowFlag = (rowId, flag, value) => {
    setCostRows((prev) =>
      prev.map((item) =>
        item.id === rowId ? { ...item, [flag]: value } : item,
      ),
    );
  };

  const [progress, setProgress] = useState("");
  const [progressMessage, setProgressMessage] = useState("");
  const [shakingFields, setShakingFields] = useState(new Set());

  const [, setHasChanges] = useState(false);
  const [
    disableAutomaticCalculationOfCharges,
    setDisableAutomaticCalculationOfCharges,
  ] = useState(
    isNewAssignment ? false : overlayData.DisableAutoChargeCalc || false,
  );

  const [createServicePerLanguage, setCreateServicePerLanguage] =
    useState(false);

  const initialDataRef = useRef(null);
  const addNewOtherServiceRef = useRef(null);
  const updateOtherServiceRef = useRef(null);
  const updateCurrentProgressRef = useRef(null);
  const saveOtherServiceRef = useRef(null);
  const attemptSaveRef = useRef(null);
  const backButtonLongPressTimerRef = useRef(null);
  const backButtonLongPressTriggeredRef = useRef(false);

  const randomInt = useCallback((min, max) => {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }, []);

  const pickRandom = useCallback((items) => {
    if (!Array.isArray(items) || items.length === 0) return null;
    return items[randomInt(0, items.length - 1)];
  }, [randomInt]);

  const pickRandomMany = useCallback((items, count) => {
    if (!Array.isArray(items) || items.length === 0 || count <= 0) return [];
    const shuffled = [...items].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, Math.min(count, shuffled.length));
  }, []);

  const clearBackButtonLongPress = useCallback(() => {
    if (backButtonLongPressTimerRef.current) {
      clearTimeout(backButtonLongPressTimerRef.current);
      backButtonLongPressTimerRef.current = null;
    }
  }, []);

  const getDateCount = useCallback(async (dateValue) => {
    if (!dateValue) {
      return 0;
    }

    const dateStr = dayjs(dateValue).format("YYYY-MM-DD");

    try {
      const response = await api.get("/services/interpretations/date-count", {
        params: { date: dateStr },
      });

      const count = parseInt(response.data.count, 10);
      return Number.isFinite(count) ? count : 0;
    } catch (error) {
      console.error("[OtherService:getDateCount] Error fetching date count:", error);
      return 0;
    }
  }, []);

  const generateProjectNumber = useCallback((dateValue, count) => {
    if (!dateValue) return "";

    const baseDate = dayjs(dateValue);
    const index = count + 1;
    const paddedIndex = index < 10 ? `0${index}` : `${index}`;
    return baseDate.format("YYMMDD") + paddedIndex;
  }, []);

  const fillDetails = useCallback((
    requestor,
    name,
    unitCount,
    targetLanguages,
    otherServiceType,
  ) => {
    const req = requestor ? requestor.trim() : "";
    const nm = name ? name.trim() : "";

    const hasRequestor = req.length > 0;
    const hasName = nm.length > 0;
    const hasUnitCount = typeof unitCount === "number" && unitCount > 0;
    const hasSelectedLanguage =
      Array.isArray(targetLanguages) && targetLanguages.length > 0;

    const detailsParts = [];

    if (hasRequestor) {
      detailsParts.push(req);
    }

    if (hasName) {
      detailsParts.push(nm);
    }

    if (hasUnitCount) {
      const selectedUnit = unitTypes.find(
        (unit) => unit.id === Number(unitTypeId),
      );
      const pluralizeUnit = (unitName, count) =>
        Number(count) === 1 ? unitName : `${unitName}s`;
      const unitLabel = pluralizeUnit(
        selectedUnit?.Name || "Unit",
        unitCount,
      ).toLowerCase();

      detailsParts.push(`${Number(unitCount).toLocaleString()} ${unitLabel}`);
    }

    let languagePart = "";

    const selectedLangNames = targetLanguages.reduce((acc, item, index) => {
      if (index === targetLanguages.length - 2) {
        return acc + item.Name + ", and ";
      } else if (index < targetLanguages.length - 1) {
        return acc + item.Name + ", ";
      } else {
        return acc + item.Name;
      }
    }, "");

    if (hasSelectedLanguage)
      languagePart += selectedLangNames + " " + otherServiceType;
    else languagePart += otherServiceType;

    detailsParts.push(languagePart);

    if (detailsParts.length > 0) {
      setDetails(detailsParts.join(" - "));
    } else {
      setDetails("");
    }
  }, [unitTypeId, unitTypes]);

  const randomizeFormForAmine = useCallback(async () => {
    if (!isAmineUser || !isNewAssignment || !isEditable) return;

    const randomDate = dayjs().add(randomInt(-30, 120), "day").startOf("day");
    const randomDeadline = randomDate.add(randomInt(1, 28), "day");
    const randomName = `${pickRandom(LOREM_NAME_SEEDS) || "Lorem ipsum"} ${randomInt(100, 999)}`;
    const randomType =
      pickRandom(otherSubtypeOptions.map((option) => option?.Name).filter(Boolean)) ||
      pickRandom(overlayData.OtherServiceTypes || []) ||
      otherServiceType;

    const randomClient = pickRandom(overlayData.Clients || []);
    const randomRequestor = pickRandom(randomClient?.Requestors || []);
    const allLanguages = overlayData.Languages || [];
    const maxTargetLanguages = Math.min(6, allLanguages.length);
    const randomTargetCount =
      maxTargetLanguages > 0 ? randomInt(1, maxTargetLanguages) : 0;
    const selectedTargets = pickRandomMany(allLanguages, randomTargetCount);

    const selectedLanguageNames = selectedTargets
      .map((language) => String(language?.Name || "").trim())
      .filter(Boolean);

    const languageMatchedLinguists = (overlayData.Linguists || []).filter(
      (linguistOption) => {
        if (String(linguistOption?.Name || "").trim().toUpperCase() === "N/A") {
          return false;
        }

        const linguistLanguages = linguistOption?.LinguistLanguages || [];
        return linguistLanguages.some((linguistLanguage) =>
          selectedLanguageNames.includes(
            String(linguistLanguage?.Language?.Name || "").trim(),
          ),
        );
      },
    );

    const randomLinguist = pickRandom(languageMatchedLinguists);

    const randomLinguistRateRaw =
      randomLinguist?.LinguistLanguages?.[0]?.Rate ?? "";
    const randomLinguistRateNum = Number(randomLinguistRateRaw);
    const randomLinguistRate = Number.isNaN(randomLinguistRateNum)
      ? ""
      : randomLinguistRateNum;

    const randomWordCount = randomInt(501, 5999);
    const unitTypePool = unitTypes.filter(
      (unit) => String(unit?.Name || "").trim().toLowerCase() !== "",
    );
    const randomUnitType = pickRandom(unitTypePool);

    setDate(randomDate);
    setDeadline(randomDeadline);
    const randomDateCount = await getDateCount(randomDate);
    setProjectNumber(generateProjectNumber(randomDate, randomDateCount));
    setProjectNumberExists(false);
    setName(randomName);
    if (randomType) setOtherServiceType(randomType);
    if (randomClient) setClient(randomClient);
    setRequestor(randomRequestor || "");
    setTargetLanguages(selectedTargets);
    setNumberOfLanguages(selectedTargets.length);
    if (randomLinguist) {
      setLinguist(randomLinguist);
      if (randomLinguistRate !== "") {
        setRate(randomLinguistRate);
      }
    } else {
      setLinguist("");
    }
    setUnitCount(randomWordCount);
    if (randomUnitType?.id) setUnitTypeId(randomUnitType.id);

    fillDetails(
      randomRequestor?.Name,
      randomName,
      randomWordCount,
      selectedTargets,
      randomType,
    );
  }, [
    fillDetails,
    isAmineUser,
    isEditable,
    isNewAssignment,
    otherServiceType,
    otherSubtypeOptions,
    overlayData.Clients,
    overlayData.Languages,
    overlayData.Linguists,
    overlayData.OtherServiceTypes,
    getDateCount,
    generateProjectNumber,
    pickRandom,
    pickRandomMany,
    randomInt,
    unitTypes,
  ]);

  const handleBackButtonPressStart = useCallback(() => {
    if (!isAmineUser || !isNewAssignment) return;
    clearBackButtonLongPress();
    backButtonLongPressTriggeredRef.current = false;
    backButtonLongPressTimerRef.current = setTimeout(() => {
      backButtonLongPressTriggeredRef.current = true;
      void randomizeFormForAmine();
    }, LONG_PRESS_AUTOFILL_MS);
  }, [
    clearBackButtonLongPress,
    isAmineUser,
    isNewAssignment,
    randomizeFormForAmine,
  ]);

  const handleBackButtonPressEnd = useCallback(() => {
    clearBackButtonLongPress();
  }, [clearBackButtonLongPress]);

  const handleBackButtonClick = useCallback(() => {
    if (backButtonLongPressTriggeredRef.current) {
      backButtonLongPressTriggeredRef.current = false;
      return;
    }

    setOverlayName("");
  }, [setOverlayName]);

  useEffect(() => {
    return () => {
      clearBackButtonLongPress();
    };
  }, [clearBackButtonLongPress]);

  // Fetch unit types on component mount
  useEffect(() => {
    api
      .get("/unitTypes")
      .then((response) => setUnitTypes(response.data || []))
      .catch((err) => console.error("Error fetching unit types:", err));
  }, []);

  // Set unit type from overlay data if editing existing service
  useEffect(() => {
    if (!isNewAssignment && overlayData.UnitTypeID && !unitTypeId) {
      setUnitTypeId(overlayData.UnitTypeID);
    }
  }, [unitTypes, unitTypeId, isNewAssignment, overlayData.UnitTypeID]);

  // Computed values for unit type display
  const selectedUnitType = unitTypes.find(
    (unit) => unit.id === Number(unitTypeId),
  );
  const unitTypeName = selectedUnitType?.Name?.toLowerCase() || "units";
  const unitTypeAbbrev = selectedUnitType?.Abbreviation || unitTypeName;

  /**
   * Helper function to get shake animation styles
   */
  const getShakeSx = (fieldId) => {
    const isShaking = shakingFields.has(fieldId);
    return isShaking
      ? {
          animation: "shake 0.6s",
          "@keyframes shake": {
            "0%, 100%": { transform: "translateX(0)" },
            "10%, 30%, 50%, 70%, 90%": { transform: "translateX(-5px)" },
            "20%, 40%, 60%, 80%": { transform: "translateX(5px)" },
          },
        }
      : {};
  };

  /**
   * Trigger shake animation on missing required fields
   */
  const triggerMissingFieldsShake = () => {
    const requiredFields = [
      { fieldId: "Date", value: date },
      { fieldId: "ProjectNumber", value: projectNumber },
      { fieldId: "OtherServiceType", value: otherServiceType },
      { fieldId: "Client", value: client },
      { fieldId: "Requestor", value: requestor },
      { fieldId: "Name", value: name },
      { fieldId: "WordCount", value: wordCount },
      { fieldId: "Rate", value: rate },
      { fieldId: "TargetLanguages", value: targetLanguages },
      { fieldId: "OtherServiceStatus", value: otherServiceStatus },
      { fieldId: "AccountCharge", value: accountCharge },
    ];

    const missingRequiredFields = requiredFields.filter(
      (field) =>
        field.value === null ||
        field.value === "" ||
        (Array.isArray(field.value) && field.value.length === 0),
    );

    if (missingRequiredFields.length > 0) {
      const newShakingFields = new Set(
        missingRequiredFields.map((field) => field.fieldId),
      );
      setShakingFields(newShakingFields);

      setTimeout(() => {
        setShakingFields(new Set());
      }, 600);

      return false;
    }

    return true;
  };

  const updateCurrentProgress = () => {
    let progress = 0;
    const total = 16;

    if (date !== null) progress++;
    if (deadline !== null) progress++;
    if (projectNumber !== null && projectNumber !== "") progress++;
    if (otherServiceType !== null && otherServiceType !== "") progress++;
    if (client !== null && client !== "") progress++;
    if (requestor !== null && requestor !== "") progress++;
    if (name !== null && name !== "") progress++;
    if (unitCount !== null && unitCount !== "") progress++;
    if (rate !== null && rate !== "") progress++;
    if (linguist !== null && linguist !== "") progress++;
    if (targetLanguages !== null && targetLanguages.length > 0) progress++;
    if (otherServiceStatus !== null && otherServiceStatus !== "") progress++;
    if (accountCharge !== null && accountCharge !== "") progress++;
    if (details !== null && details !== "") progress++;
    if ((totalCost !== null && totalCost !== "") || !completing) progress++;
    if ((linguistPay !== null && linguistPay !== "") || !completing) progress++;

    const percentProgress = (progress * 100) / total;
    const message =
      progress === total
        ? "All Done!"
        : `${progress}/${total} fields completed`;
    setProgressMessage(Number(percentProgress).toFixed(2) + "%: " + message);
    setProgress(percentProgress);
  };

  // Helper function to generate details string for a single language (for per-language services)
  const generateDetailsForLanguage = (
    requestorName,
    serviceName,
    unitCount,
    targetLanguageName,
    otherServiceType,
  ) => {
    const req = requestorName ? requestorName.trim() : "";
    const nm = serviceName ? serviceName.trim() : "";

    const hasRequestor = req.length > 0;
    const hasName = nm.length > 0;
    const hasUnitCount = typeof unitCount === "number" && unitCount > 0;
    const hasTargetLanguage =
      targetLanguageName && targetLanguageName.trim().length > 0;

    const detailsParts = [];

    if (hasRequestor) {
      detailsParts.push(req);
    }

    if (hasName) {
      detailsParts.push(nm);
    }

    if (hasUnitCount) {
      const pluralizeUnit = (name, count) =>
        Number(count) === 1 ? name : `${name}s`;
      const unitLabel = pluralizeUnit(
        selectedUnitType?.Name || "Unit",
        unitCount,
      ).toLowerCase();

      detailsParts.push(`${Number(unitCount).toLocaleString()} ${unitLabel}`);
    }

    let languagePart = "";
    if (hasTargetLanguage) {
      languagePart = `${targetLanguageName} ${otherServiceType}`;
    } else {
      languagePart = otherServiceType;
    }

    detailsParts.push(languagePart);

    return detailsParts.length > 0 ? detailsParts.join(" - ") : "";
  };

  dayjs.extend(utc);
  dayjs.extend(durationPlugin);

  // Auto-calculate charges based on word count, rate, and other factors
  useEffect(() => {
    // If automatic calculation is disabled, skip it
    if (disableAutomaticCalculationOfCharges) {
      return;
    }

    const parsedUnitCount = Number(unitCount);
    const parsedRate = Number(rate);
    const parsedUnitRate = Number(unitRate);
    const parsedLanguageCount = Number(numberOfLanguages);

    const hasValidUnitCount =
      unitCount !== null && unitCount !== "" && Number.isFinite(parsedUnitCount);
    const hasValidRate =
      rate !== null && rate !== "" && Number.isFinite(parsedRate);
    const hasValidUnitRate =
      unitRate !== null && unitRate !== "" && Number.isFinite(parsedUnitRate);
    const hasValidLanguageCount =
      numberOfLanguages !== null && Number.isFinite(parsedLanguageCount);

    // UnitRate takes precedence; fall back to Rate for backward compat
    const effectiveRate = hasValidUnitRate ? parsedUnitRate : parsedRate;

    // If required fields are missing/invalid, don't calculate
    if (
      !hasValidUnitCount ||
      (!hasValidRate && !hasValidUnitRate) ||
      !hasValidLanguageCount
    ) {
      return;
    }

    // Use 1 for calculations when creating service per language
    const effectiveNumberOfLanguages =
      createServicePerLanguage && targetLanguages.length > 1
        ? 1
        : numberOfLanguages;

    // Per-row inclusion flags drive the totals (flag-driven, matches Settings).
    const rowFlagByType = costRows.reduce((acc, row) => {
      acc[row.typeKey] = {
        includeInTotalCost: row.includeInTotalCost,
        includeInLinguistPay: row.includeInLinguistPay,
        includeInAccountCharge: row.includeInAccountCharge,
      };
      return acc;
    }, {});

    const getRowFlag = (typeKey, flag, fallback) => {
      const rowFlags = rowFlagByType[typeKey];
      return rowFlags && typeof rowFlags[flag] === "boolean"
        ? rowFlags[flag]
        : fallback;
    };

    const chargeExtras = costTypeOptions.filter(
      (opt) =>
        opt.key !== "linguistPay" &&
        getRowFlag(
          opt.key,
          "includeInAccountCharge",
          Boolean(opt.effectiveIncludeInAccountCharge),
        ),
    );
    const extraChargeSum = chargeExtras.reduce(
      (sum, opt) => sum + parseFloat(getCostValueByType(opt.key) || 0),
      0,
    );
    const extraChargeText = chargeExtras
      .map((opt) => parseFloat(getCostValueByType(opt.key) || 0).toFixed(2))
      .join(" + ");

    const lingPayExtras = costTypeOptions.filter(
      (opt) =>
        opt.key !== "linguistPay" &&
        getRowFlag(
          opt.key,
          "includeInLinguistPay",
          Boolean(opt.effectiveIncludeInLinguistPay),
        ),
    );
    const extraLingPaySum = lingPayExtras.reduce(
      (sum, opt) => sum + parseFloat(getCostValueByType(opt.key) || 0),
      0,
    );
    const extraLingPayText = lingPayExtras
      .map((opt) => parseFloat(getCostValueByType(opt.key) || 0).toFixed(2))
      .join(" + ");

    const totCostExtras = costTypeOptions.filter(
      (opt) =>
        opt.key !== "linguistPay" &&
        getRowFlag(
          opt.key,
          "includeInTotalCost",
          Boolean(opt.effectiveIncludeInTotalCost),
        ),
    );
    const extraTotCostSum = totCostExtras.reduce(
      (sum, opt) => sum + parseFloat(getCostValueByType(opt.key) || 0),
      0,
    );
    const extraTotCostText = totCostExtras
      .map((opt) => parseFloat(getCostValueByType(opt.key) || 0).toFixed(2))
      .join(" + ");

    let charge =
      parseFloat(effectiveNumberOfLanguages) *
        parsedUnitCount *
        effectiveRate +
      extraChargeSum;

    const chargeText =
      "( " +
      parseFloat(effectiveNumberOfLanguages) +
      " x " +
      parsedUnitCount +
      " x " +
      effectiveRate +
      " )" +
      (extraChargeText ? " + " + extraChargeText : "");

    const totalLinguistPayValue =
      parseFloat(linguistPay || 0) + extraLingPaySum;

    const totCost = parseFloat(linguistPay || 0) + extraTotCostSum;

    const totCostText =
      parseFloat(linguistPay || 0) +
      (extraTotCostText ? " + " + extraTotCostText : "");

    // Apply $49 minimum for automatic calculations
    charge = charge >= 49 ? charge : 49;
    setAccountCharge(Number(charge).toFixed(2));
    setAccountChargeHelperText(chargeText);

    setTotalCostHelperText(totCostText);
    setTotalLinguistPay(Number(totalLinguistPayValue).toFixed(2));
    setTotalCost(Number(totCost).toFixed(2));

    const prof = parseFloat(charge) - parseFloat(totCost);
    setProfit(prof.toFixed(2));
    // Only calculate margin if charge is not zero
    if (charge === 0) {
      setMargin(0);
    } else {
      const marginValue = (prof / charge) * 100;
      setMargin(
        isNaN(marginValue) || !isFinite(marginValue)
          ? 0
          : marginValue.toFixed(2),
      );
    }
  }, [
    unitCount,
    numberOfLanguages,
    rate,
    unitRate,
    additionalLinguistCost,
    additionalCost,
    deliveryCost,
    paymentFees,
    linguistPay,
    costRows,
    costTypeOptions,
    customCostValuesByType,
    disableAutomaticCalculationOfCharges,
    createServicePerLanguage,
    targetLanguages,
  ]);

  // Recalculate profit and margin whenever accountCharge or totalCost changes
  useEffect(() => {
    const charge = parseFloat(accountCharge || 0);
    const totCost = parseFloat(totalCost || 0);
    const prof = parseFloat(charge) - parseFloat(totCost);

    setProfit(prof.toFixed(2));
    // Only calculate margin if charge is not zero
    if (charge === 0) {
      setMargin(0);
    } else {
      const marginValue = (prof / charge) * 100;
      setMargin(
        isNaN(marginValue) || !isFinite(marginValue)
          ? 0
          : marginValue.toFixed(2),
      );
    }
  }, [accountCharge, totalCost]);

  const validateFields = () => {
    setNameError(name === null);
    setDateError(date === null);
    setProjectNumberError(projectNumber === null);
    setOtherServiceTypeError(otherServiceType === null);
    setClientError(client === null);
    setRequestorError(requestor === null);
    setLinguistError(linguist === null);
    setRateError(rate === null);
    setOtherServiceStatusError(otherServiceStatus === null);
    setAccountChargeError(accountCharge === null);
    setTotalCostError(totalCost === null);
    setLinguistPayError(linguistPay === null);
    setDetailsError(details === null);
  };

  // Check if assignment was paid and if critical fields have changed
  const checkPaidWarning = () => {
    // Only check for existing assignments
    if (isNewAssignment) return false;

    // Check if the assignment was paid
    const wasPaid = overlayData.Paid === true || overlayData.Paid === 1;

    if (!wasPaid) return false;

    // Check if critical fields have changed
    const criticalChanges = [];

    // Check if client changed
    if (client?.id !== overlayData.Client?.id) {
      criticalChanges.push("Client");
    }

    // Check if linguist changed
    if (linguist?.id !== overlayData.Linguist?.id) {
      criticalChanges.push("Linguist");
    }

    // Check if account charge changed
    if (Number(accountCharge) !== Number(overlayData.AccountCharge)) {
      criticalChanges.push("Account Charge");
    }

    // Check if total cost changed
    if (Number(totalCost) !== Number(overlayData.TotalCost)) {
      criticalChanges.push("Total Cost");
    }

    // Check if linguist pay changed
    if (Number(linguistPay) !== Number(overlayData.LinguistPay)) {
      criticalChanges.push("Linguist Pay");
    }

    if (criticalChanges.length > 0) {
      let message = "⚠️ WARNING: This assignment has been marked as PAID.\n\n";
      message += `You are changing: ${criticalChanges.join(", ")}\n\n`;

      // Special check for linguist change - check if the linguist was paid
      if (criticalChanges.includes("Linguist")) {
        message +=
          "🔹 You are changing the linguist for this paid assignment.\n";
        message += `   Previous linguist: ${overlayData.Linguist?.Name || "None"}\n`;
        message += `   New linguist: ${linguist?.Name || "None"}\n\n`;
      }

      message += "Are you sure you want to proceed with these changes?";

      setPaidWarningMessage(message);
      return true;
    }

    return false;
  };

  const buildDynamicCostLines = () => {
    const toAmount = (value) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? Number(parsed.toFixed(2)) : 0;
    };

    const lineConfigByType = costTypeOptions.reduce((acc, option) => {
      acc[option.key] = {
        CostTypeName: option.costTypeName,
        SourceLegacyField: option.sourceLegacyField,
        IncludeInTotalCost: option.effectiveIncludeInTotalCost,
        IncludeInAccountCharge:
          option.key === "paymentFees"
            ? false
            : option.effectiveIncludeInAccountCharge,
        IncludeInLinguistPay: option.effectiveIncludeInLinguistPay ?? false,
      };
      return acc;
    }, {});

    // Per-row flags win over the type-level defaults.
    const rowFlagByType = costRows.reduce((acc, row) => {
      acc[row.typeKey] = {
        IncludeInTotalCost: row.includeInTotalCost,
        IncludeInLinguistPay: row.includeInLinguistPay,
        IncludeInAccountCharge: row.includeInAccountCharge,
      };
      return acc;
    }, {});

    const applyRowFlags = (config, typeKey) => {
      const rowFlags = rowFlagByType[typeKey];
      if (!rowFlags) return config;
      return {
        ...config,
        IncludeInTotalCost: rowFlags.IncludeInTotalCost,
        IncludeInLinguistPay: rowFlags.IncludeInLinguistPay,
        IncludeInAccountCharge:
          typeKey === "paymentFees" ? false : rowFlags.IncludeInAccountCharge,
      };
    };

    const selectedTypeKeys = costRows
      .map((row) => row.typeKey)
      .filter(Boolean);

    const nonZeroTypeKeys = costTypeOptions
      .map((option) => option.key)
      .filter((key) => Number(getCostValueByType(key) || 0) !== 0)
      .filter((key) => !selectedTypeKeys.includes(key));

    const orderedTypeKeys = [...selectedTypeKeys, ...nonZeroTypeKeys];

    return orderedTypeKeys
      .map((typeKey, index) => {
        const config = lineConfigByType[typeKey];
        if (!config) return null;

        return {
          ...applyRowFlags(config, typeKey),
          Amount: toAmount(getCostValueByType(typeKey)),
          DisplayOrder: (index + 1) * 10,
        };
      })
      .filter(Boolean)
      .filter((line) => line.Amount > 0);
  };

  function addNewOtherService() {
    setIsSavingChanges(true);
    setIsEditable(false);

    const dynamicCostLines = buildDynamicCostLines();
    const assignmentCreationSource =
      overlayData?.CreationSource === "quick_create"
        ? "quick_create"
        : "screen_button";

    // Check if we should create one service per language
    if (createServicePerLanguage && targetLanguages.length > 1) {
      // Create one service for each target language
      const servicePromises = targetLanguages.map((targetLanguage, index) => {
        const currentProjectNumber = String(Number(projectNumber) + index);

        // Generate details specific to this language
        const languageSpecificDetails = generateDetailsForLanguage(
          requestor?.Name || "",
          name,
          Number(unitCount),
          targetLanguage.Name,
          otherServiceType,
        );

        const data = {
          Assignment: {
            id: 0,
            Date: date.format("YYYY-MM-DD"),
            Deadline:
              deadline && deadline.isValid()
                ? deadline.format("YYYY-MM-DD")
                : null,
            DeliveryDate:
              deliveryDate && deliveryDate.isValid()
                ? deliveryDate.format("YYYY-MM-DD")
                : null,
            ProjectNumber: currentProjectNumber,
            CheckingDate: null,
            ServiceCategory: "Other",
            Name: name,
            Details: languageSpecificDetails,
            OtherServiceStatus:
              otherServiceStatus === "" ? null : otherServiceStatus,
            AccountCharge: accountCharge === "" ? null : accountCharge,
            TotalCost: totalCost === "" ? null : totalCost,
            TotalLinguistPay:
              totalLinguistPay === ""
                ? null
                : Number(totalLinguistPay).toFixed(2),
            AdditionalLinguistCost:
              additionalLinguistCost === "" ? null : additionalLinguistCost,
            AdditionalCost: additionalCost === "" ? null : additionalCost,
            LinguistPay: linguistPay === "" ? null : linguistPay,
            DeliveryCost: deliveryCost === "" ? null : deliveryCost,
            PaymentFees: paymentFees === "" ? null : paymentFees,
            DeliveryType: deliveryType === "" ? null : deliveryType,
            PaymentType: paymentType === "" ? null : paymentType,
            Profit: profit === "" ? null : profit,
            Margin: margin === "" ? null : margin,
            OtherServiceType: otherServiceType,
            ServiceSubtypeID: resolveOtherSubtypeId(otherServiceType),
            Paid: false,
            Rate: rate === "" ? null : rate,
            UnitTypeID: Number(unitTypeId) || null,
            UnitCount: Number(unitCount) || null,
            UnitRate: unitRate === "" ? null : Number(unitRate),
            WordCount:
              unitTypeName === "hours" ? null : Number(unitCount) || null,
            Deleted: false,
            Cancelled: false,
            NumberOfLanguages: 1, // Single language per service
            DisableAutoChargeCalc: disableAutomaticCalculationOfCharges,
            Invoiced: false,
            CancellationReason: null,
            CreationSource: assignmentCreationSource,
            CreatedBy: currentUser.id,
            Checker: null,
          },
          TargetLanguages: [targetLanguage], // Only this language
          Client: client,
          Requestor: requestor,
          Linguist: linguist,
          Invoicer: invoiceTo,
          CostLines: dynamicCostLines,
        };

        return api.post("/services/otherServices/addNewOtherService", {
          data: data,
        });
      });

      // Wait for all services to be created
      Promise.all(servicePromises)
        .then((responses) => {
          // Mark save complete and refresh UI immediately
          setIsSavingChanges(false);
          triggerPageRefresh(
            `${responses.length} Services Added Successfully!`,
            theme,
          );

          // Emit assignment saved event for other viewers for each service
          if (socket) {
            responses.forEach((response) => {
              const projectNum = response.data.ProjectNumber || "";
              socket.emit("assignment:saved", {
                modelType: "OtherService",
                recordId: response.data.ServiceID || response.data.id,
                projectNumber: projectNum,
                user: {
                  id: currentUser.id,
                  name: currentUser.Name,
                  imageThumbnail: currentUser.ImageThumbnail,
                },
              });
            });

            socket.emit("assignment:saved", {
              modelType: "OtherServicesList",
              recordId: "all",
              serviceId:
                responses[0]?.data?.ServiceID || responses[0]?.data?.id || null,
              projectNumber: projectNumber,
              user: {
                id: currentUser.id,
                name: currentUser.Name,
                imageThumbnail: currentUser.ImageThumbnail,
              },
            });
          }

          setOverlayName("");
        })
        .catch((err) => {
          console.log(err);
          setIsSavingChanges(false);
          setIsEditable(true);
        });

      return;
    }

    // Original behavior: create one service with all languages
    const data = {
      Assignment: {
        id: 0,
        Date: date.format("YYYY-MM-DD"),
        Deadline:
          deadline && deadline.isValid() ? deadline.format("YYYY-MM-DD") : null,
        DeliveryDate:
          deliveryDate && deliveryDate.isValid()
            ? deliveryDate.format("YYYY-MM-DD")
            : null,
        ProjectNumber: projectNumber,
        CheckingDate: null,
        ServiceCategory: "Other",
        Name: name,
        Details: details,
        OtherServiceStatus:
          otherServiceStatus === "" ? null : otherServiceStatus,
        AccountCharge: accountCharge === "" ? null : accountCharge,
        TotalCost: totalCost === "" ? null : totalCost,
        TotalLinguistPay:
          totalLinguistPay === "" ? null : Number(totalLinguistPay).toFixed(2),
        AdditionalLinguistCost:
          additionalLinguistCost === "" ? null : additionalLinguistCost,
        AdditionalCost: additionalCost === "" ? null : additionalCost,
        LinguistPay: linguistPay === "" ? null : linguistPay,
        DeliveryCost: deliveryCost === "" ? null : deliveryCost,
        PaymentFees: paymentFees === "" ? null : paymentFees,
        DeliveryType: deliveryType === "" ? null : deliveryType,
        PaymentType: paymentType === "" ? null : paymentType,
        Profit: profit === "" ? null : profit,
        Margin: margin === "" ? null : margin,
        OtherServiceType: otherServiceType,
        ServiceSubtypeID: resolveOtherSubtypeId(otherServiceType),
        Paid: false,
        Rate: rate === "" ? null : rate,
        UnitTypeID: Number(unitTypeId) || null,
        UnitCount: Number(unitCount) || null,
        UnitRate: unitRate === "" ? null : Number(unitRate),
        WordCount: unitTypeName === "hours" ? null : Number(unitCount) || null,
        Deleted: false,
        Cancelled: false,
        NumberOfLanguages: targetLanguages.length,
        DisableAutoChargeCalc: disableAutomaticCalculationOfCharges,
        Invoiced: false,
        CancellationReason: null,
        CreationSource: assignmentCreationSource,
        CreatedBy: currentUser.id,
        Checker: null,
      },
      TargetLanguages: targetLanguages,
      Client: client,
      Requestor: requestor,
      Linguist: linguist,
      Invoicer: invoiceTo,
      CostLines: dynamicCostLines,
    };
    api
      .post("/services/otherServices/addNewOtherService", {
        data: data,
      })
      .then((response) => {
        setIsSavingChanges(false);
        triggerPageRefresh("New Other Service Added Successfully!", theme, {
          label: "Open",
          onClick: () => navigate(`/otherServices/${response.data.id}`),
        });
        // Emit assignment saved event for other viewers
        if (socket) {
          const projectNum = projectNumber || response.data.ProjectNumber || "";
          socket.emit("assignment:saved", {
            modelType: "OtherService",
            recordId: response.data.ServiceID || response.data.id,
            projectNumber: projectNum,
            user: {
              id: currentUser.id,
              name: currentUser.Name,
              imageThumbnail: currentUser.ImageThumbnail,
            },
          });
          socket.emit("assignment:saved", {
            modelType: "OtherServicesList",
            recordId: "all",
            serviceId: response.data.ServiceID || response.data.id,
            projectNumber: projectNum,
            user: {
              id: currentUser.id,
              name: currentUser.Name,
              imageThumbnail: currentUser.ImageThumbnail,
            },
          });
        }

        setOverlayName("");
      })
      .catch((err) => console.log(err));
  }

  function updateOtherService() {
    setIsSavingChanges(true);
    setIsEditable(false);

    const dynamicCostLines = buildDynamicCostLines();

    const dataOld = {
      Assignment: {
        id: overlayData.id,
        Date: overlayData.Date,
        Deadline: overlayData.Deadline,
        DeliveryDate: overlayData.DeliveryDate,
        ProjectNumber: overlayData.ProjectNumber,
        CheckingDate: null,
        ServiceCategory: "Other",
        Name: overlayData.Name,
        Details: overlayData.Details,
        OtherServiceStatus:
          overlayData.OtherServiceStatus === ""
            ? null
            : overlayData.OtherServiceStatus,
        AccountCharge:
          overlayData.AccountCharge === "" ? null : overlayData.AccountCharge,
        TotalCost: overlayData.TotalCost === "" ? null : overlayData.TotalCost,
        TotalLinguistPay:
          overlayData.TotalLinguistPay === "" || overlayData.TotalLinguistPay == null
            ? null
            : Number(overlayData.TotalLinguistPay).toFixed(2),
        AdditionalLinguistCost:
          overlayData.AdditionalLinguistCost === "" ||
          overlayData.AdditionalLinguistCost === null
            ? null
            : Number(overlayData.AdditionalLinguistCost).toFixed(2),
        AdditionalCost:
          overlayData.AdditionalCost === "" ||
          overlayData.AdditionalCost === null
            ? "0.00"
            : overlayData.AdditionalCost,
        LinguistPay:
          overlayData.LinguistPay === "" ? null : overlayData.LinguistPay,
        DeliveryCost:
          overlayData.DeliveryCost === "" ? null : overlayData.DeliveryCost,
        PaymentFees:
          overlayData.PaymentFees === "" ? null : overlayData.PaymentFees,
        DeliveryType:
          overlayData.DeliveryType === "" ? null : overlayData.DeliveryType,
        PaymentType:
          overlayData.PaymentType === "" ? null : overlayData.PaymentType,
        Profit: overlayData.Profit === "" ? null : overlayData.Profit,
        Margin: overlayData.Margin === "" ? null : overlayData.Margin,
        OtherServiceType:
          (overlayData.ServiceSubtype?.Name || "") === ""
            ? null
            : overlayData.ServiceSubtype?.Name,
        ServiceSubtypeID:
          overlayData.ServiceSubtypeID === "" || overlayData.ServiceSubtypeID == null
            ? null
            : Number(overlayData.ServiceSubtypeID),
        Paid: false,
        Rate: overlayData.Rate === "" ? null : overlayData.Rate,
        UnitRate: overlayData.UnitRate === "" ? null : overlayData.UnitRate,
        WordCount: overlayData.WordCount === "" ? null : overlayData.WordCount,
        NumberOfLanguages:
          overlayData.NumberOfLanguages === ""
            ? null
            : overlayData.NumberOfLanguages,
        DisableAutoChargeCalc: overlayData.DisableAutoChargeCalc || false,
        Deleted: false,
        Cancelled: false,
        Invoiced: false,
        CancellationReason: null,
        CreatedBy: currentUser.id,
        Checker: null,
      },
      TargetLanguages: overlayData.ServiceTargetLanguages,
      Client: overlayData.Client,
      Requestor: overlayData.Requestor,
      Linguist: overlayData.Linguist,
      Invoicer: overlayData.Invoicer,
    };

    const dataNew = {
      Assignment: {
        id: overlayData.id,
        Date: date.format("YYYY-MM-DD"),
        Deadline:
          deadline && deadline.isValid() ? deadline.format("YYYY-MM-DD") : null,
        DeliveryDate:
          deliveryDate && deliveryDate.isValid()
            ? deliveryDate.format("YYYY-MM-DD")
            : null,
        ProjectNumber: projectNumber,
        CheckingDate: null,
        ServiceCategory: "Other",
        Name: name,
        Details: details,
        OtherServiceStatus:
          otherServiceStatus === "" ? null : otherServiceStatus,
        AccountCharge:
          accountCharge === "" ? null : Number(accountCharge).toFixed(2),
        TotalCost: totalCost === "" ? null : Number(totalCost).toFixed(2),
        TotalLinguistPay:
          totalLinguistPay === "" ? null : Number(totalLinguistPay).toFixed(2),
        AdditionalLinguistCost:
          additionalLinguistCost === ""
            ? null
            : Number(additionalLinguistCost).toFixed(2),
        AdditionalCost:
          additionalCost === "" ? null : Number(additionalCost).toFixed(2),
        LinguistPay: linguistPay === "" ? null : Number(linguistPay).toFixed(2),
        DeliveryCost: deliveryCost === "" ? null : deliveryCost,
        PaymentFees: paymentFees === "" ? null : paymentFees,
        DeliveryType: deliveryType === "" ? null : deliveryType,
        PaymentType: paymentType === "" ? null : paymentType,
        Profit: profit === "" ? null : Number(profit).toFixed(2),
        Margin: margin === "" ? null : Number(margin).toFixed(2),
        OtherServiceType: otherServiceType,
        ServiceSubtypeID: resolveOtherSubtypeId(otherServiceType),
        Paid: false,
        Rate: rate === "" ? null : Number(rate).toFixed(2),
        UnitTypeID: Number(unitTypeId) || null,
        UnitCount: Number(unitCount) || null,
        UnitRate: unitRate === "" ? null : Number(unitRate),
        WordCount: unitTypeName === "hours" ? null : Number(unitCount) || null,
        NumberOfLanguages: targetLanguages.length,
        DisableAutoChargeCalc: disableAutomaticCalculationOfCharges,
        Deleted: false,
        Cancelled: false,
        Invoiced: false,
        CancellationReason: null,
        CreatedBy: currentUser.id,
        Checker: null,
      },
      TargetLanguages: targetLanguages,
      Client: client,
      Requestor: requestor,
      Linguist: linguist,
      Invoicer: invoiceTo,
      CostLines: dynamicCostLines,
    };

    const allData = {
      Old: dataOld,
      New: dataNew,
    };

    api
      .post("/services/otherServices/updateOtherService", {
        data: allData,
      })
      .then((response) => {
        setIsSavingChanges(false);
        triggerPageRefresh(
          "Other Service Assignment Updated Sucessfully",
          theme,
        );

        // Emit assignment saved event for other viewers
        if (socket) {
          socket.emit("assignment:saved", {
            modelType: "OtherService",
            recordId: overlayData.id,
            projectNumber: projectNumber,
            user: {
              id: currentUser.id,
              name: currentUser.Name,
              imageThumbnail: currentUser.ImageThumbnail,
            },
          });
          socket.emit("assignment:saved", {
            modelType: "OtherServicesList",
            recordId: "all",
            serviceId: overlayData.id,
            projectNumber: projectNumber,
            user: {
              id: currentUser.id,
              name: currentUser.Name,
              imageThumbnail: currentUser.ImageThumbnail,
            },
          });
        }

        setOverlayName("");
      })
      .catch((err) => {
        console.error("Error updating Other Service:", err);
        console.error("Error details:", {
          message: err.message,
          code: err.code,
        });
        setIsSavingChanges(false);
        setIsEditable(true);
        alert("Failed to update Other Service. Check console for details.");
      });
  }

  useEffect(() => {
    if (shouldValidate) {
      if (
        !(
          dateError ||
          projectNumberError ||
          nameError ||
          otherServiceTypeError ||
          clientError ||
          requestorError ||
          linguistError ||
          rateError ||
          otherServiceStatusError ||
          accountChargeError ||
          totalCostError ||
          linguistPayError ||
          detailsError
        )
      ) {
        const invalidFlagRows = costRows.filter(
          (row) =>
            Number(getCostValueByType(row.typeKey) || 0) > 0 &&
            !row.includeInTotalCost &&
            !row.includeInLinguistPay &&
            !row.includeInAccountCharge,
        );

        if (invalidFlagRows.length > 0) {
          openDialog({
            theme,
            title: "Cost Line Inclusion Required",
            message:
              "Each cost line must be included in at least one total (Total Cost, Total Linguist Pay, or Account Charge).",
            primaryAction: "OK",
            primaryOnClick: closeDialog,
          });
        } else {
          isNewAssignment
            ? addNewOtherServiceRef.current()
            : updateOtherServiceRef.current();
        }
      }
      setShouldValidate(false);
    }
  }, [
    shouldValidate,
    nameError,
    dateError,
    projectNumberError,
    otherServiceTypeError,
    clientError,
    requestorError,
    linguistError,
    rateError,
    otherServiceStatusError,
    accountChargeError,
    totalCostError,
    linguistPayError,
    detailsError,
    isNewAssignment,
    costRows,
  ]);

  useEffect(() => {
    updateCurrentProgressRef.current();
  }, [
    name,
    date,
    deadline,
    projectNumber,
    otherServiceType,
    client,
    requestor,
    linguist,
    rate,
    unitTypeId,
    unitCount,
    otherServiceStatus,
    targetLanguages,
    accountCharge,
    totalCost,
    linguistPay,
    details,
  ]);

  useEffect(() => {
    if (!isNewAssignment) {
      initialDataRef.current = {
        name: overlayData.Name,
        date: overlayData.Date,
        deadline: overlayData.Deadline,
        deliveryDate: overlayData.DeliveryDate,
        projectNumber: overlayData.ProjectNumber,
        otherServiceType: overlayData.ServiceSubtype?.Name,
        client: overlayData.Client,
        requestor: overlayData.Requestor,
        invoiceTo: overlayData.Invoicer,
        targetLanguages: overlayData.ServiceTargetLanguages,
        linguist: overlayData.Linguist,
        otherServiceStatus: overlayData.OtherServiceStatus,
        accountCharge: overlayData.AccountCharge,
        totalCost: overlayData.TotalCost,
        linguistPay: overlayData.LinguistPay,
        additionalLinguistCost: overlayData.AdditionalLinguistCost,
        additionalCost: overlayData.AdditionalCost,
        unitTypeId: overlayData.UnitTypeID || overlayData.UnitType?.id,
        unitCount: overlayData.UnitCount || overlayData.WordCount,
        rate: overlayData.Rate,
      };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isNewAssignment && initialDataRef.current) {
      const languageList = (initialDataRef.current.targetLanguages || []).map(
        (targetLanguage) => targetLanguage.Language,
      );

      const dirty =
        name !== initialDataRef.current.name ||
        (date && date.format("YYYY-MM-DD")) !== initialDataRef.current.date ||
        (deadline && deadline.format("YYYY-MM-DD")) !==
          initialDataRef.current.deadline ||
        Number(projectNumber) !==
          Number(initialDataRef.current.projectNumber) ||
        otherServiceType !== initialDataRef.current.otherServiceType ||
        client?.Account !== initialDataRef.current.client?.Account ||
        requestor?.Name !== initialDataRef.current.requestor?.Name ||
        invoiceTo?.Name !== initialDataRef.current.invoiceTo?.Name ||
        JSON.stringify(targetLanguages) !== JSON.stringify(languageList) ||
        linguist?.Name !== initialDataRef.current.linguist?.Name ||
        otherServiceStatus !== initialDataRef.current.otherServiceStatus ||
        Number(accountCharge) !==
          Number(initialDataRef.current.accountCharge) ||
        Number(totalCost) !== Number(initialDataRef.current.totalCost) ||
        Number(linguistPay) !== Number(initialDataRef.current.linguistPay) ||
        Number(additionalLinguistCost) !==
          Number(initialDataRef.current.additionalLinguistCost) ||
        Number(additionalCost) !==
          Number(initialDataRef.current.additionalCost) ||
        Number(unitTypeId) !== Number(initialDataRef.current.unitTypeId) ||
        (Number(unitCount) || 0) !==
          (Number(initialDataRef.current.unitCount) || 0) ||
        Number(rate) !== Number(initialDataRef.current.rate);
      setHasChanges(dirty);
      // Mirror into the ref so the socket refetch handler sees current dirtiness.
      hasChangesRef.current = dirty;
    }
  }, [
    name,
    date,
    deadline,
    projectNumber,
    otherServiceType,
    client,
    requestor,
    invoiceTo,
    targetLanguages,
    linguist,
    otherServiceStatus,
    accountCharge,
    totalCost,
    linguistPay,
    additionalLinguistCost,
    additionalCost,
    unitCount,
    unitTypeId,
    isNewAssignment,
    rate,
  ]);

  const saveOtherService = () => {
    // Check if we need to show paid warning
    if (!pendingSave && checkPaidWarning()) {
      setShowPaidWarning(true);
      return;
    }

    // If we reach here, either no warning needed or user confirmed
    setPendingSave(false);
    validateFields();
    setShouldValidate(true);
  };

  const attemptSave = () => {
    if (isSavingChanges || isLocked) {
      return;
    }

    if (isNewAssignment && !triggerMissingFieldsShake()) {
      return;
    }

    if (isNewAssignment && projectNumberExists) {
      openDialog({
        theme,
        title: "Duplicate Project Number",
        message: `The project number ${projectNumber} already exists. Create another assignment with the same number?`,
        primaryAction: "Create Anyway",
        secondaryAction: "Cancel",
        primaryOnClick: () => {
          closeDialog();
          saveOtherService();
        },
        secondaryOnClick: closeDialog,
      });
      return;
    }

    saveOtherService();
  };

  // Handle confirmation of paid assignment warning
  const handlePaidWarningConfirm = () => {
    setShowPaidWarning(false);
    setPendingSave(true);
    // Trigger save again, but this time with pendingSave = true to bypass the check
    setTimeout(() => saveOtherService(), 0);
  };

  // Handle cancellation of paid assignment warning
  const handlePaidWarningCancel = () => {
    setShowPaidWarning(false);
    setPendingSave(false);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      const target = e.target;
      const tagName = target?.tagName;
      const isTextArea = tagName === "TEXTAREA";
      const isContentEditable = !!target?.isContentEditable;
      const isInsideDialog =
        target instanceof Element && !!target.closest('[role="dialog"]');
      const isInsidePopup =
        target instanceof Element &&
        (!!target.closest('[role="listbox"]') ||
          !!target.closest(".MuiAutocomplete-popper") ||
          !!target.closest(".MuiPopover-root") ||
          !!target.closest(".MuiMenu-root"));

      if (
        e.key === "Enter" &&
        !e.shiftKey &&
        !isTextArea &&
        !isContentEditable &&
        !isInsideDialog &&
        !isInsidePopup
      ) {
        e.preventDefault();
        attemptSaveRef.current();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  addNewOtherServiceRef.current = addNewOtherService;
  updateOtherServiceRef.current = updateOtherService;
  updateCurrentProgressRef.current = updateCurrentProgress;
  saveOtherServiceRef.current = saveOtherService;
  attemptSaveRef.current = attemptSave;

  return (
    <>
    <Overlay
      id="other-service-new-edit"
      open={true}
      onClose={handleBackButtonClick}
      title={
        isNewAssignment
          ? "New Other Service Assignment"
          : completing
            ? "Complete Other Service Assignment"
            : "Edit Other Service Assignment"
      }
      warnings={[
        ...(paidLocked
          ? [
              {
                type: "error",
                message:
                  "Editing is disabled for services on a paid invoice. Un-mark the invoice as paid first to edit this service.",
              },
            ]
          : []),
      ]}
      backButtonProps={{
        onMouseDown: handleBackButtonPressStart,
        onMouseUp: handleBackButtonPressEnd,
        onMouseLeave: handleBackButtonPressEnd,
        onTouchStart: handleBackButtonPressStart,
        onTouchEnd: handleBackButtonPressEnd,
        onTouchCancel: handleBackButtonPressEnd,
      }}
      footer={
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
              width: "50%",
              flexDirection: "row",
            }}
          >
            <Typography
              variant="h5"
              sx={{
                color:
                  progress < 50
                    ? theme.palette.error[600]
                    : progress < 100
                      ? theme.palette.warning[500]
                      : theme.palette.primary.main,
              }}
            >
              {progressMessage}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "end",
              alignItems: "flex-end",
              width: "50%",
              flexDirection: "row",
            }}
          >
            {isMobile ? (
              <>
                <Tooltip title="Discard" placement="top">
                  <IconButton
                    color="primary"
                    onClick={() => setOverlayName("")}
                    sx={{
                      width: "40px",
                      height: "40px",
                      border: `2px solid ${theme.palette.primary.main}`,
                      borderRadius: "12px",
                      "&:hover": {
                        backgroundColor: theme.palette.primary.light,
                      },
                    }}
                  >
                    <DisabledByDefaultRoundedIcon />
                  </IconButton>
                </Tooltip>

                <Box p="0 .5rem" />

                <Tooltip title="Save" placement="top">
                  <span>
                    <IconButton
                      disabled={isSavingChanges || isLocked || paidLocked}
                      onClick={attemptSave}
                      sx={{
                        width: "40px",
                        height: "40px",
                        backgroundColor: theme.palette.primary.main,
                        color: "#fff",
                        borderRadius: "12px",
                        "&:hover": {
                          backgroundColor: theme.palette.primary.dark,
                        },
                        "&.Mui-disabled": {
                          backgroundColor:
                            theme.palette.action.disabledBackground,
                          color: theme.palette.action.disabled,
                        },
                      }}
                    >
                      <SaveRoundedIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            ) : (
              <>
                <Button
                  color="primary"
                  variant="outlined"
                  size="large"
                  endIcon={<DisabledByDefaultRoundedIcon />}
                  onClick={() => setOverlayName("")}
                  sx={{
                    borderRadius: "12px",
                  }}
                >
                  Discard
                </Button>

                <Box p="0 .5rem" />

                <Button
                  loading={isSavingChanges}
                  disabled={isSavingChanges || isLocked || paidLocked}
                  color="primary"
                  variant="contained"
                  size="large"
                  endIcon={<SaveRoundedIcon />}
                  type="submit"
                  onClick={attemptSave}
                  sx={{
                    borderRadius: "12px",
                  }}
                >
                  Save
                </Button>
              </>
            )}
          </Box>
        </>
      }
    >
      <Box
        sx={{
          position: "relative",
          flex: 1,
          minHeight: 0,
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          component="form"
          sx={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            p: "1rem 2.3rem 1rem 2.5rem",
            height: "100%",
            overflowY: "auto",
            width: "100%",
            "& .MuiTextField-root": {
              m: 0,
              width: "100%",
            },
          }}
        >
          <Grid
            container
            spacing={2}
            width="100%"
            alignItems="flex-start"
            alignContent="flex-start"
          >
            {completing ? null : (
              <>
                <Grid size={{ xs: 12, md: 6 }}>
                <MobileDatePicker
                  disabled={!isEditable}
                  id={"Date"}
                  label="Date"
                  size="small"
                  format="dddd, MMMM DD, YYYY"
                  value={date}
                  onChange={(newValue) => {
                    setDate(newValue);
                    setDateError(newValue === null);
                  }}
                  sx={{
                    ...getShakeSx("Date"),
                    "& .MuiOutlinedInput-root": {
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                      },
                      "&.MuiInputBase-root fieldset": {
                        py: 0,
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,
                          (date && date.format("YYYY-MM-DD")) || "",
                          overlayData.Date,
                        ),
                      },
                    },
                  }}
                  slotProps={{
                    textField: {
                      size: "small",
                      error: dateError,
                    },
                  }}
                />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <MobileDatePicker
                    disabled={!isEditable}
                    id={"Deadline"}
                    label="Deadline"
                    size="small"
                    format="dddd, MMMM DD, YYYY"
                    value={deadline}
                    onChange={(newValue) => {
                      setDeadline(newValue);
                      setDeadlineError(newValue === null);
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& .MuiInputBase-input": {
                          padding: "8.5px 12px !important",
                        },
                        "&.MuiInputBase-root fieldset": {
                          py: 0,
                          borderColor: getBorderColor(
                            theme,
                            isNewAssignment,
                            (deadline && deadline.format("YYYY-MM-DD")) || "",
                            overlayData.Deadline,
                          ),
                        },
                      },
                    }}
                    slotProps={{
                      textField: {
                        size: "small",
                        error: dateError,
                      },
                    }}
                  />
                </Grid>
              </>
            )}
            {completing ? null : (
              <>
                <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  disabled={!isEditable}
                  size="small"
                  id={"ProjectNumber"}
                  label="Project Number"
                  error={projectNumberError || projectNumberExists}
                  value={projectNumber}
                  onChange={(event) => {
                    setProjectNumber(event.target.value);
                    setProjectNumberError(event.target.value === null);
                    setProjectNumberExists(false);
                  }}
                  onBlur={async () => {
                    if (projectNumber && isNewAssignment) {
                      try {
                        const response = await api.get(
                          `/services/check-project-number?projectNumber=${projectNumber}`,
                        );
                        setProjectNumberExists(response.data.exists);
                      } catch (error) {
                        console.error("Error checking project number:", error);
                      }
                    }
                  }}
                  helperText={
                    projectNumberExists
                      ? "This project number already exists (Ignore if duplicate)."
                      : ""
                  }
                  sx={{
                    ...getShakeSx("ProjectNumber"),
                    "& .MuiOutlinedInput-root": {
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                      },
                      "&.MuiInputBase-root fieldset": {
                        py: 0,
                        borderColor: projectNumberExists
                          ? theme.palette.warning.main
                          : getBorderColor(
                              theme,
                              isNewAssignment,
                              Number(projectNumber) || "",
                            overlayData.ServiceSubtype?.Name,
                            ),
                      },
                    },
                  }}
                />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    disabled={!isEditable}
                    size="small"
                    select
                    id={"OtherServiceType"}
                    label="Service Type"
                    error={otherServiceTypeError}
                    value={otherServiceType}
                    onChange={(event) => {
                      setOtherServiceType(event.target.value);
                      setOtherServiceTypeError(event.target.value === null);

                      fillDetails(
                        requestor.Name,
                        name,
                        Number(unitCount),
                        targetLanguages,
                        event.target.value,
                      );
                    }}
                    sx={{
                      ...getShakeSx("OtherServiceType"),
                      "& .MuiOutlinedInput-root": {
                        "& .MuiInputBase-input": {
                          padding: "8.5px 12px !important",
                        },
                        "&.MuiInputBase-root fieldset": {
                          py: 0,
                          borderColor: getBorderColor(
                            theme,
                            isNewAssignment,
                            otherServiceType || "",
                            overlayData.OtherServiceType,
                          ),
                        },
                      },
                    }}
                  >
                    {overlayData.OtherServiceTypes.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </>
            )}

            <>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                disabled={!isEditable}
                size="small"
                id={"Name"}
                label="Name"
                error={nameError}
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                  setNameError(event.target.value === null);

                  fillDetails(
                    requestor.Name,
                    event.target.value,
                    Number(wordCount),
                    targetLanguages,
                    otherServiceType,
                  );
                }}
                sx={{
                  ...getShakeSx("Name"),
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                      borderColor: getBorderColor(
                        theme,
                        isNewAssignment,
                        name || "",
                        overlayData.Name,
                      ),
                    },
                  },
                }}
              />
              </Grid>

              {completing ? null : (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Autocomplete
                  disabled={!isEditable}
                  disableClearable
                  key={"Client"}
                  size="small"
                  options={overlayData.Clients}
                  getOptionLabel={(client) => client?.Account || ""}
                  value={client}
                  onChange={(event, newValue) => {
                    if (typeof newValue === "string") {
                      setClient({
                        Account: newValue,
                      });
                      setAddNewClient(false);
                    } else if (newValue && newValue.inputValue) {
                      // Create a new value from the user input
                      setClient({
                        Account: newValue.inputValue,
                      });

                      setAddNewClient(true);
                    } else {
                      setClient(newValue);
                      setAddNewClient(false);
                    }

                    setRequestor("");

                    setClientError(newValue === null);
                  }}
                  filterOptions={(options, params) => {
                    const { inputValue } = params;

                    // Filter by both Account and ShortName
                    const filtered = options.filter((option) => {
                      const accountMatch =
                        option.Account?.toLowerCase().includes(
                          inputValue.toLowerCase(),
                        );
                      const shortNameMatch =
                        option.ShortName?.toLowerCase().includes(
                          inputValue.toLowerCase(),
                        );
                      return accountMatch || shortNameMatch;
                    });

                    // Suggest the creation of a new value
                    const isExisting = options.some(
                      (option) =>
                        inputValue === option.Account ||
                        inputValue === option.ShortName,
                    );
                    if (inputValue !== "" && !isExisting) {
                      filtered.push({
                        inputValue,
                        Account: `Add "${inputValue}"`,
                      });
                    }

                    return filtered;
                  }}
                  selectOnFocus
                  clearOnBlur
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Client"
                      placeholder=""
                      error={clientError}
                    />
                  )}
                  sx={{
                    ...getShakeSx("Client"),
                    "& .MuiOutlinedInput-root": {
                      "&.MuiInputBase-root fieldset": {
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,
                          (client && client.Account) || "",
                          (overlayData.Client && overlayData.Client.Account) ||
                            "",
                        ),
                      },
                    },
                  }}
                />
                </Grid>
              )}
            </>
            {completing ? null : (
              <>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Autocomplete
                  disabled={!isEditable}
                  disableClearable
                  key={"Requestor"}
                  size="small"
                  id={"Requestor"}
                  options={(client && client.Requestors) || []}
                  getOptionLabel={(requestor) => requestor?.Name || ""}
                  value={requestor}
                  onChange={(event, newValue) => {
                    if (typeof newValue === "string") {
                      const nextRequestor = { Name: newValue };
                      setRequestor(nextRequestor);
                      setAddNewRequestor(false);
                      // Default the invoice recipient to the requestor.
                      applyRequestorInvoiceDefaults(nextRequestor);
                      fillDetails(
                        newValue,
                        name,
                        Number(unitCount),
                        targetLanguages,
                        otherServiceType,
                      );
                    } else if (newValue && newValue.inputValue) {
                      // Create a new value from the user input
                      const nextRequestor = { Name: newValue.inputValue };
                      setRequestor(nextRequestor);
                      setAddNewRequestor(true);
                      // Default the invoice recipient to the requestor.
                      applyRequestorInvoiceDefaults(nextRequestor);
                      fillDetails(
                        newValue.inputValue,
                        name,
                        Number(unitCount),
                        targetLanguages,
                        otherServiceType,
                      );
                    } else {
                      setRequestor(newValue);
                      setAddNewRequestor(false);
                      // Auto-populate invoice recipient + CC from the
                      // requestor's group (or default to the requestor).
                      applyRequestorInvoiceDefaults(newValue);
                      fillDetails(
                        newValue.Name,
                        name,
                        Number(unitCount),
                        targetLanguages,
                        otherServiceType,
                      );
                    }

                    setRequestorError(newValue === null);
                  }}
                  filterOptions={(options, params) => {
                    const filtered = filter(options, params);

                    const { inputValue } = params;
                    // Suggest the creation of a new value
                    const isExisting = options.some(
                      (option) => inputValue === option.Name,
                    );
                    if (inputValue !== "" && !isExisting) {
                      filtered.push({
                        inputValue,
                        Name: `Add "${inputValue}"`,
                      });
                    }

                    return filtered;
                  }}
                  selectOnFocus
                  clearOnBlur
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Requestor"
                      placeholder=""
                      error={requestorError}
                    />
                  )}
                  sx={{
                    ...getShakeSx("Requestor"),
                    "& .MuiOutlinedInput-root": {
                      "&.MuiInputBase-root fieldset": {
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,
                          (requestor && requestor.Name) || "",
                          (overlayData &&
                            overlayData.Requestor &&
                            overlayData.Requestor.Name) ||
                            "",
                        ),
                      },
                    },
                  }}
                />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Autocomplete
                disabled={!isEditable}
                disableClearable
                key={"InvoiceTo"}
                size="small"
                id={"InvoiceTo"}
                options={(client && client.Requestors) || []}
                getOptionLabel={(requestor) => requestor?.Name || ""}
                value={invoiceTo}
                onChange={(event, newValue) => {
                  if (typeof newValue === "string") {
                    setInvoiceTo({
                      Name: newValue,
                    });
                    setAddNewInvoiceTo(false);
                  } else if (newValue && newValue.inputValue) {
                    // Create a new value from the user input
                    setInvoiceTo({
                      Name: newValue.inputValue,
                    });
                    setAddNewInvoiceTo(true);
                  } else {
                    setInvoiceTo(newValue);
                    setAddNewInvoiceTo(false);
                  }

                  setInvoiceToError(newValue === null);
                }}
                filterOptions={(options, params) => {
                  const filtered = filter(options, params);

                  const { inputValue } = params;
                  // Suggest the creation of a new value
                  const isExisting = options.some(
                    (option) => inputValue === option.Name,
                  );
                  if (inputValue !== "" && !isExisting) {
                    filtered.push({
                      inputValue,
                      Name: `Add "${inputValue}"`,
                    });
                  }

                  return filtered;
                }}
                selectOnFocus
                clearOnBlur
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Invoice To"
                    placeholder=""
                    error={invoiceToError}
                  />
                )}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.MuiInputBase-root fieldset": {
                      borderColor: getBorderColor(
                        theme,
                        isNewAssignment,
                        (invoiceTo && invoiceTo.Name) || "",
                        (overlayData.Invoicer && overlayData.Invoicer.Name) ||
                          "",
                        true,
                      ),
                    },
                  },
                }}
              />
              {ccRequestors.length > 0 && (
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 0.5,
                    color: theme.palette.text.secondary,
                  }}
                >
                  CC: {ccRequestors.map((cc) => cc?.Name).join(", ")}
                </Typography>
              )}
                </Grid>
              </>
            )}

            <>
              {completing ? null : (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Autocomplete
                  disabled={!isEditable}
                  disableClearable
                  key={"Linguist"}
                  size="small"
                  options={linguistOptions}
                  getOptionLabel={(linguist) => linguist?.Name || ""}
                  isOptionEqualToValue={(option, value) => {
                    const optionId = Number(option?.id || 0);
                    const valueId = Number(value?.id || 0);

                    if (optionId > 0 && valueId > 0) {
                      return optionId === valueId;
                    }

                    return (option?.Name || "") === (value?.Name || "");
                  }}
                  value={linguist}
                  renderOption={renderLinguistOption}
                  onChange={(event, newValue) => {
                    if (typeof newValue === "string") {
                      setLinguist({
                        Name: newValue,
                      });
                    } else if (newValue && newValue.inputValue) {
                      // Create a new value from the user input
                      setLinguist({
                        Name: newValue.inputValue,
                      });
                    } else {
                      setLinguist(newValue);
                    }

                    setLinguistError(newValue === null);
                  }}
                  filterOptions={(options, params) => {
                    const filtered = filter(options, params);

                    const { inputValue } = params;
                    // Suggest the creation of a new value — EXCEPT "N/A":
                    // N/A is the reserved placeholder linguist (canonical id 1)
                    // and must never be created (Amine: don't show "Add N/A").
                    const isExisting = options.some(
                      (option) => inputValue === option.Name,
                    );
                    if (
                      inputValue !== "" &&
                      !isExisting &&
                      inputValue.trim().toUpperCase() !== "N/A"
                    ) {
                      filtered.push({
                        inputValue,
                        Name: `Add "${inputValue}"`,
                      });
                    }

                    return filtered;
                  }}
                  selectOnFocus
                  clearOnBlur
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Linguist"
                      placeholder=""
                      error={linguistError}
                    />
                  )}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&.MuiInputBase-root fieldset": {
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,
                          linguist?.Name || "",
                          (overlayData?.Linguist &&
                            overlayData.Linguist?.Name) ||
                            "",
                        ),
                      },
                    },
                  }}
                />
                </Grid>
              )}

              <Grid size={completing ? { xs: 12, md: 6 } : { xs: 12, md: 3 }}>
                <TextField
                disabled={!isEditable}
                size="small"
                id={"Rate"}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  },
                }}
                label="Rate"
                error={rateError}
                value={rate}
                onChange={(event) => {
                  setRate(event.target.value);
                  setRateError(event.target.value === null);
                }}
                sx={{
                  ...getShakeSx("Rate"),
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                      borderColor: getBorderColor(
                        theme,
                        isNewAssignment,
                        Number(rate) || "",
                        Number(overlayData.Rate),
                      ),
                    },
                  },
                }}
              />
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <TextField
                disabled={!isEditable}
                select
                size="small"
                id="UnitType"
                label="Unit Type"
                value={unitTypeId}
                onChange={(event) => {
                  setUnitTypeId(Number(event.target.value));
                  fillDetails(
                    requestor.Name,
                    name,
                    Number(unitCount),
                    targetLanguages,
                    otherServiceType,
                  );
                }}
                sx={{
                  ...getShakeSx("UnitType"),
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                      borderColor: getBorderColor(
                        theme,
                        isNewAssignment,
                        Number(unitTypeId) || "",
                        Number(overlayData.UnitType?.id) || "",
                        true,
                      ),
                    },
                  },
                }}
              >
                {unitTypes.map((unit) => (
                  <MenuItem key={unit.id} value={unit.id}>
                    {unit.Name}
                  </MenuItem>
                ))}
              </TextField>
              </Grid>
            </>

            <>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                disabled={!isEditable}
                size="small"
                id="UnitCount"
                label="Unit Count"
                error={wordCountError}
                value={unitCount}
                onChange={(event) => {
                  setUnitCount(event.target.value);
                  setWordCountError(event.target.value === null);
                  fillDetails(
                    requestor.Name,
                    name,
                    Number(event.target.value),
                    targetLanguages,
                    otherServiceType,
                  );
                }}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        {unitTypeAbbrev}
                      </InputAdornment>
                    ),
                  },
                }}
                sx={{
                  ...getShakeSx("UnitCount"),
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                      borderColor: getBorderColor(
                        theme,
                        isNewAssignment,
                        Number(unitCount) || "",
                        Number(
                          overlayData.UnitCount || overlayData.WordCount,
                        ) || "",
                        true,
                      ),
                    },
                  },
                }}
              />
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <TextField
                disabled={!isEditable}
                size="small"
                id="UnitRate"
                label="Unit Rate"
                value={unitRate}
                onChange={(event) => {
                  setUnitRate(event.target.value);
                }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        {`Per ${unitTypeAbbrev}`}
                      </InputAdornment>
                    ),
                  },
                }}
                sx={{
                  ...getShakeSx("UnitRate"),
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                      borderColor: getBorderColor(
                        theme,
                        isNewAssignment,
                        Number(unitRate) || "",
                        Number(overlayData.UnitRate) || "",
                        true,
                      ),
                    },
                  },
                }}
              />
              </Grid>

              <Grid size={completing ? { xs: 12, md: 6 } : { xs: 12, md: 4 }}>
                <TextField
                disabled={!isEditable}
                size="small"
                select
                id={"OtherServiceStatus"}
                label="Service Status"
                error={otherServiceStatusError}
                //defaultValue={otherServiceStatus === null ? "Requested" : ""}
                value={otherServiceStatus}
                onChange={(event) => {
                  setOtherServiceStatus(event.target.value);
                  setOtherServiceStatusError(event.target.value === null);
                }}
                sx={{
                  ...getShakeSx("OtherServiceStatus"),
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                      borderColor: getBorderColor(
                        theme,
                        isNewAssignment,
                        otherServiceStatus || "",
                        overlayData.OtherServiceStatus,
                      ),
                    },
                  },
                }}
              >
                {overlayData.OtherServiceStatuses.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
              </Grid>

              <Grid size={completing ? { xs: 12, md: 6 } : { xs: 12, md: 4 }}>
                <MobileDatePicker
                disabled={!isEditable}
                id={"DeliveryDate"}
                label="Delivery Date"
                size="small"
                format="dddd, MMMM DD, YYYY"
                value={deliveryDate}
                onChange={(newValue) => {
                  setDeliveryDate(newValue);
                  setDeliveryDateError(newValue === null);
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                      borderColor: getBorderColor(
                        theme,
                        isNewAssignment,
                        (deliveryDate && deliveryDate.format("YYYY-MM-DD")) ||
                          "",
                        overlayData.DeliveryDate,
                      ),
                    },
                  },
                }}
                slotProps={{
                  textField: {
                    size: "small",
                    error: deliveryDateError,
                  },
                }}
              />
              </Grid>
            </>

            <Grid size={{ xs: 12 }}>
              <Autocomplete
                slots={{
                  popper: CustomPopper, // Use the custom Popper component
                }}
                multiple
                size="small"
                id="checkboxes-tags-demo"
                options={languageOptions}
                disableCloseOnSelect
                getOptionLabel={(option) => option.Name}
                //getoptionselected={(option, value) => option.id === value.id} // Add this line
                value={targetLanguages}
                onChange={(event, newValue) => {
                  const occurrenceMap = newValue.reduce((acc, item) => {
                    acc[item.id] = (acc[item.id] || 0) + 1;
                    return acc;
                  }, {});

                  const uniqueValues = newValue.filter(
                    (item) => occurrenceMap[item.id] === 1,
                  );

                  // Add a default Rate of 99 to items without a Rate
                  const updatedValues = uniqueValues.map((item) => ({
                    ...item,
                  }));

                  fillDetails(
                    requestor?.Name,
                    name,
                    Number(unitCount),
                    updatedValues,
                    otherServiceType,
                  );

                  const numOfLangs = updatedValues.length;

                  setNumberOfLanguages(numOfLangs);

                  setTargetLanguages(updatedValues);
                }}
                renderOption={(props, option, { selected }) => {
                  const { key, ...optionProps } = props;
                  const isSelected = targetLanguages.some(
                    (lang) => lang.id === option.id,
                  ); // Check if the option is selected
                  const stats =
                    languageClientStatsById[Number(option?.id || 0)] ||
                    EMPTY_LANGUAGE_CLIENT_STATS;

                  return (
                    <li key={key} {...optionProps}>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        style={{ marginRight: 8 }}
                        checked={isSelected}
                      />
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          width: "100%",
                          gap: 1,
                        }}
                      >
                        <Box component="span" sx={{ minWidth: 0 }}>
                          {option.Name}
                        </Box>
                        {hasSelectedClient ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ ml: "auto", textAlign: "right", whiteSpace: "nowrap" }}
                          >
                            {`Other Services: ${stats.requestCount}`}
                          </Typography>
                        ) : null}
                      </Box>
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={
                      !targetLanguages || targetLanguages.length === 0
                        ? "Target Languages"
                        : "Target Languages: " + targetLanguages.length
                    }
                    placeholder="Type or select from the list"
                    sx={{
                      ...getShakeSx("TargetLanguages"),
                      "& .MuiOutlinedInput-root": {
                        "&.MuiInputBase-root fieldset": {
                          borderColor: getBorderColor(
                            theme,
                            isNewAssignment,
                            (targetLanguages && targetLanguages.length > 0) ||
                              "",
                            (overlayData.ServiceTargetLanguages &&
                              overlayData.ServiceTargetLanguages.length > 0) ||
                              "",
                            true,
                          ),
                        },
                      },
                    }}
                  />
                )}
              />
            </Grid>

            {showLinguistPayAndOtherCosts ? (
              <Grid size={{ xs: 12 }}>
                <Box
                  sx={{
                    mb: 0,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <Typography variant="subtitle2">Costs</Typography>
                  <IconButton
                    size="small"
                    onClick={addCostRow}
                    disabled={
                      !isEditable ||
                      costRows.length >=
                        costTypeOptions.filter((option) => option.isEnabled).length
                    }
                    sx={{ p: 0.5 }}
                  >
                    <AddRoundedIcon fontSize="small" />
                  </IconButton>
                  <Divider sx={{ flex: 1, ml: 0.5 }} />
                </Box>
              </Grid>
            ) : null}

            {showLinguistPayAndOtherCosts && costRows.length > 0 ? (
              <Grid container spacing={2} size={{ xs: 12 }}>
                {costRows.map((row) => {
                  const usedKeys = new Set(
                    costRows
                      .filter((item) => item.id !== row.id)
                      .map((item) => item.typeKey),
                  );
                  const isLinguistPayRow = row.typeKey === "linguistPay";
                  const isPaymentFeesRow = row.typeKey === "paymentFees";
                  const inclusionChanged =
                    row.includeInTotalCost !== row.originalIncludeInTotalCost ||
                    row.includeInLinguistPay !== row.originalIncludeInLinguistPay ||
                    row.includeInAccountCharge !== row.originalIncludeInAccountCharge;
                  const checkedFlagCount = [
                    row.includeInTotalCost,
                    row.includeInLinguistPay,
                    row.includeInAccountCharge,
                  ].filter(Boolean).length;
                  const isLastCheckedFlag = (flag) =>
                    Boolean(flag) && checkedFlagCount === 1;

                  return (
                    <Grid size={{ xs: 12 }} key={`cost-row-${row.id}`}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1.5,
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Grid container spacing={1.5}>
                            <Grid size={{ xs: 12, md: "grow" }}>
                              <TextField
                                disabled={!isEditable}
                                select
                                size="small"
                                label="Cost Type"
                                value={row.typeKey}
                                onChange={(event) =>
                                  updateCostRowType(row.id, event.target.value)
                                }
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    "& .MuiInputBase-input": {
                                      padding: "8.5px 12px !important",
                                    },
                                    "&.MuiInputBase-root fieldset": {
                                      py: 0,
                                      borderColor: getBorderColor(
                                        theme,
                                        isNewAssignment,
                                        row.typeKey || "",
                                        getOriginalCostTypeByRowId(row.id) || "",
                                        true,
                                      ),
                                    },
                                  },
                                }}
                              >
                                {costTypeOptions
                                  .filter(
                                    (option) =>
                                      option.key === row.typeKey ||
                                      (option.isEnabled && !usedKeys.has(option.key)),
                                  )
                                  .map((option) => (
                                    <MenuItem key={option.key} value={option.key}>
                                      {option.label}
                                    </MenuItem>
                                  ))}
                              </TextField>
                            </Grid>
                            <Grid size={{ xs: 12, md: "grow" }}>
                              <TextField
                                disabled={!isEditable}
                                size="small"
                                label="Amount"
                                value={getCostValueByType(row.typeKey)}
                                onChange={(event) =>
                                  setCostValueByType(row.typeKey, event.target.value)
                                }
                                slotProps={{
                                  input: {
                                    startAdornment: (
                                      <InputAdornment position="start">$</InputAdornment>
                                    ),
                                  },
                                }}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    "& .MuiInputBase-input": {
                                      padding: "8.5px 12px !important",
                                    },
                                    "&.MuiInputBase-root fieldset": {
                                      py: 0,
                                      borderColor: getBorderColor(
                                        theme,
                                        isNewAssignment,
                                        Number(getCostValueByType(row.typeKey)) || "",
                                        Number(getOriginalCostValueByType(row.typeKey)) || "",
                                        true,
                                      ),
                                    },
                                  },
                                }}
                              />
                            </Grid>
                            <Grid size={{ xs: 12, md: "grow" }}>
                              <TextField
                                select
                                disabled={!isEditable}
                                size="small"
                                label="Include in"
                                slotProps={{
                                  select: {
                                    multiple: true,
                                    value: [
                                      ...(row.includeInTotalCost
                                        ? ["includeInTotalCost"]
                                        : []),
                                      ...(isLinguistPayRow ||
                                      row.includeInLinguistPay
                                        ? ["includeInLinguistPay"]
                                        : []),
                                      ...(row.includeInAccountCharge
                                        ? ["includeInAccountCharge"]
                                        : []),
                                    ],
                                    onChange: (event) => {
                                      const raw = event.target.value;
                                      const value = Array.isArray(raw)
                                        ? raw
                                        : raw == null
                                          ? []
                                          : [raw];
                                      if (value.length === 0) return;
                                      const next = new Set(value);
                                      if (
                                        row.includeInTotalCost !==
                                        next.has("includeInTotalCost")
                                      ) {
                                        updateCostRowFlag(
                                          row.id,
                                          "includeInTotalCost",
                                          next.has("includeInTotalCost"),
                                        );
                                      }
                                      if (
                                        !isLinguistPayRow &&
                                        row.includeInLinguistPay !==
                                          next.has("includeInLinguistPay")
                                      ) {
                                        updateCostRowFlag(
                                          row.id,
                                          "includeInLinguistPay",
                                          next.has("includeInLinguistPay"),
                                        );
                                      }
                                      if (
                                        row.includeInAccountCharge !==
                                        next.has("includeInAccountCharge")
                                      ) {
                                        updateCostRowFlag(
                                          row.id,
                                          "includeInAccountCharge",
                                          next.has("includeInAccountCharge"),
                                        );
                                      }
                                    },
                                    renderValue: (selected) =>
                                      selected.length === 3
                                        ? "All"
                                        : selected
                                            .map(
                                              (v) =>
                                                ({
                                                  includeInTotalCost:
                                                    "Total Cost",
                                                  includeInLinguistPay:
                                                    "Total Linguist Pay",
                                                  includeInAccountCharge:
                                                    "Account Charge",
                                                })[v],
                                            )
                                            .filter(Boolean)
                                            .join(", "),
                                  },
                                }}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    "& .MuiInputBase-input": {
                                      padding: "8.5px 12px !important",
                                    },
                                    "&.MuiInputBase-root fieldset": {
                                      py: 0,
                                      borderColor: getBorderColor(
                                        theme,
                                        isNewAssignment,
                                        inclusionChanged ? "changed" : "unchanged",
                                        "unchanged",
                                      ),
                                    },
                                  },
                                }}
                              >
                                <MenuItem
                                  value="includeInTotalCost"
                                  disabled={
                                    !isEditable ||
                                    isLinguistPayRow ||
                                    isLastCheckedFlag(row.includeInTotalCost)
                                  }
                                >
                                  <Checkbox
                                    size="small"
                                    checked={Boolean(row.includeInTotalCost)}
                                    disabled={
                                      !isEditable ||
                                      isLinguistPayRow ||
                                      isLastCheckedFlag(row.includeInTotalCost)
                                    }
                                  />
                                  Total Cost
                                </MenuItem>
                                <MenuItem
                                  value="includeInLinguistPay"
                                  disabled={!isEditable || isLinguistPayRow}
                                >
                                  <Checkbox
                                    size="small"
                                    checked={
                                      isLinguistPayRow
                                        ? true
                                        : Boolean(row.includeInLinguistPay)
                                    }
                                    disabled={!isEditable || isLinguistPayRow}
                                  />
                                  Total Linguist Pay
                                </MenuItem>
                                <MenuItem
                                  value="includeInAccountCharge"
                                  disabled={
                                    !isEditable ||
                                    isLinguistPayRow ||
                                    isPaymentFeesRow ||
                                    isLastCheckedFlag(
                                      row.includeInAccountCharge,
                                    )
                                  }
                                >
                                  <Checkbox
                                    size="small"
                                    checked={Boolean(
                                      row.includeInAccountCharge,
                                    )}
                                    disabled={
                                      !isEditable ||
                                      isLinguistPayRow ||
                                      isPaymentFeesRow ||
                                      isLastCheckedFlag(
                                        row.includeInAccountCharge,
                                      )
                                    }
                                  />
                                  Account Charge
                                </MenuItem>
                              </TextField>
                            </Grid>
                          </Grid>
                        </Box>
                        <Box sx={{ flex: "0 0 auto", mt: { xs: 0, md: 0.5 } }}>
                          <IconButton
                            disabled={!isEditable}
                            onClick={() => removeCostRow(row.id)}
                            size="small"
                          >
                            <ClearRoundedIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            ) : null}

            {showLinguistPayAndOtherCosts ? (
              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 0.5 }} />
              </Grid>
            ) : null}

            {showLinguistPayAndOtherCosts ? (
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  disabled={!isEditable}
                  size="small"
                  id={"TotalLinguistPay"}
                  label="Total Linguist Pay"
                  slotProps={{
                    input: {
                      readOnly: !disableAutomaticCalculationOfCharges,
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    },
                  }}
                  value={totalLinguistPay}
                  onChange={(event) => {
                    setTotalLinguistPay(event.target.value);
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                      },
                      "&.MuiInputBase-root fieldset": {
                        py: 0,
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,
                          Number(totalLinguistPay) || "",
                          Number(overlayData.TotalLinguistPay),
                          !completing,
                        ),
                      },
                    },
                  }}
                />
              </Grid>
            ) : null}

            {completing ? (
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  disabled={!isEditable}
                  size="small"
                  id={"PaymentType"}
                  label="Payment Type"
                  select
                  error={paymentTypeError}
                  value={paymentType || ""}
                  onChange={(event) => {
                    setPaymentType(event.target.value);
                    setPaymentTypeError(event.target.value === null);
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                      },
                      "&.MuiInputBase-root fieldset": {
                        py: 0,
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,
                          paymentType || "",
                          overlayData.PaymentType || "",
                        ),
                      },
                    },
                  }}
                >
                  {(overlayData.PaymentTypes || []).map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            ) : null}

            {completing ? (
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  disabled={!isEditable}
                  size="small"
                  id={"DeliveryType"}
                  label="Delivery Type"
                  select
                  error={deliveryTypeError}
                  value={deliveryType || ""}
                  onChange={(event) => {
                    setDeliveryType(event.target.value);
                    setDeliveryTypeError(event.target.value === null);
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                      },
                      "&.MuiInputBase-root fieldset": {
                        py: 0,
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,
                          deliveryType || "",
                          overlayData.DeliveryType || "",
                        ),
                      },
                    },
                  }}
                >
                  {(overlayData.DeliveryTypes || []).map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            ) : null}



            {showLinguistPayAndOtherCosts ? (
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  disabled={!isEditable}
                  size="small"
                  id={"TotalCost"}
                  label="Total Cost"
                  slotProps={{
                    input: {
                      readOnly: !disableAutomaticCalculationOfCharges,
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    },
                  }}
                  helperText={
                    disableAutomaticCalculationOfCharges
                      ? null
                      : totalCostHelperText
                  }
                  error={totalCostError}
                  value={totalCost}
                  onChange={(event) => {
                    setTotalCost(event.target.value);
                    setTotalCostError(event.target.value === null);
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                      },
                      "&.MuiInputBase-root fieldset": {
                        py: 0,
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,
                          Number(totalCost) || "",
                          Number(overlayData.TotalCost),
                          !completing,
                        ),
                      },
                    },
                  }}
                />
              </Grid>
            ) : null}

            <Grid size={{ xs: 12 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                id={"AccountCharge"}
                label="Account Charge"
                slotProps={{
                  input: {
                    readOnly: !disableAutomaticCalculationOfCharges,
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  },
                }}
                helperText={
                  disableAutomaticCalculationOfCharges
                    ? null
                    : accountChargeHelperText
                }
                error={accountChargeError}
                value={accountCharge}
                onChange={(event) => {
                  setAccountCharge(event.target.value);
                  setAccountChargeError(event.target.value === null);

                  const accountChargeValue = parseFloat(event.target.value);
                  const totalCostValue = parseFloat(totalCost);

                  // Prevent division by zero and handle invalid values
                  if (
                    !event.target.value ||
                    accountChargeValue === 0 ||
                    isNaN(accountChargeValue) ||
                    isNaN(totalCostValue)
                  ) {
                    setProfit(0);
                    setMargin(0);
                    return;
                  }

                  const prof = accountChargeValue - totalCostValue;
                  setProfit(prof.toFixed(2));
                  const calculatedMargin = (prof / accountChargeValue) * 100;
                  setMargin(
                    isNaN(calculatedMargin) || !isFinite(calculatedMargin)
                      ? 0
                      : calculatedMargin.toFixed(2),
                  );
                }}
                sx={{
                  ...getShakeSx("AccountCharge"),
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                      borderColor: getBorderColor(
                        theme,
                        isNewAssignment,
                        Number(accountCharge) || "",
                        Number(overlayData.AccountCharge),
                      ),
                    },
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                disabled={isEditable ? !allowDetailsEditing : true}
                size="small"
                id={"Details"}
                label="Details"
                error={detailsError}
                value={details}
                onChange={(event) => {
                  setDetails(event.target.value);
                  setDetailsError(event.target.value === null);
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                    },
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                disabled={true}
                size="small"
                id={"Profit"}
                label="Profit"
                value={profit}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                      borderColor:
                        details === null || details === ""
                          ? "gray"
                          : theme.palette.primary.main,
                    },
                  },
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                disabled={true}
                size="small"
                id={"Margin"}
                label="Margin"
                value={margin}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">%</InputAdornment>
                    ),
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                      borderColor:
                        details === null || details === ""
                          ? "gray"
                          : theme.palette.primary.main,
                    },
                  },
                }}
              />
            </Grid>

            <Grid container size={{ xs: 12 }} spacing={1.5} sx={{ pb: 1 }}>
              <Grid size={{ xs: 12 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Options
                </Typography>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Paper
                  variant="outlined"
                  sx={{ p: 1.5, borderRadius: "10px", height: "100%" }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    Manual Overrides
                  </Typography>

                  <HtmlTooltip
                    title={
                      <Box p=".5rem">
                        <Typography
                          color="inherit"
                          style={{ whiteSpace: "pre-line" }}
                        >
                          {"Yes: Allow the editing of the details."}
                          {`\n\nNo: Details will be not-editable, and will be filled according to Name, Requestor, and Language.`}
                        </Typography>
                      </Box>
                    }
                  >
                    <Box sx={{ py: 0.25 }}>
                      <FormControlLabel
                        control={<Switch sx={optionSwitchSx} />}
                        id={"AllowDetailsEditing"}
                        label="Edit Details Manually"
                        checked={allowDetailsEditing}
                        onChange={(event) => {
                          setAllowDetailsEditing(event.target.checked);

                          if (!event.target.checked)
                            fillDetails(
                              requestor.Name,
                              name,
                              Number(unitCount),
                              targetLanguages,
                              otherServiceType,
                            );
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ display: "block", ml: 4.5, color: theme.palette.text.secondary }}
                      >
                        Enable direct edits instead of generated details text.
                      </Typography>
                    </Box>
                  </HtmlTooltip>

                  <HtmlTooltip
                    title={
                      <Box p=".5rem">
                        <Typography
                          color="inherit"
                          style={{ whiteSpace: "pre-line" }}
                        >
                          {
                            "Yes: Automatic calculation of Total Cost and Account Charge will be disabled."
                          }
                          {`\n\nNo: Automatic calculation of Total Cost and Account Charge will be enabled.`}
                        </Typography>
                      </Box>
                    }
                  >
                    <Box sx={{ py: 0.25 }}>
                      <FormControlLabel
                        control={<Switch sx={optionSwitchSx} />}
                        id={"disableAutomaticCalculationOfCharges"}
                        label="Disable Automatic Charge Calculation"
                        checked={disableAutomaticCalculationOfCharges}
                        onChange={(event) => {
                          setDisableAutomaticCalculationOfCharges(
                            event.target.checked,
                          );
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ display: "block", ml: 4.5, color: theme.palette.text.secondary }}
                      >
                        Manage cost and charge values manually for this service.
                      </Typography>
                    </Box>
                  </HtmlTooltip>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Paper
                  variant="outlined"
                  sx={{ p: 1.5, borderRadius: "10px", height: "100%" }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    Visibility & Creation
                  </Typography>

                  <HtmlTooltip
                    title={
                      <Box p=".5rem">
                        <Typography
                          color="inherit"
                          style={{ whiteSpace: "pre-line" }}
                        >
                          {
                            "Yes: Show: Linguist Pay - Additional Linguist Cost - Additional Costs - Delviery Cost - Payment Fees"
                          }
                          {`\n\nNo: Hide: Linguist Pay - Additional Linguist Cost - Additional Costs - Delviery Cost - Payment Fees`}
                        </Typography>
                      </Box>
                    }
                  >
                    <Box sx={{ py: 0.25 }}>
                      <FormControlLabel
                        control={<Switch sx={optionSwitchSx} />}
                        id={"showLinguistPayAndOtherCosts"}
                        label="Show Linguist Pay & Other Costs"
                        checked={showLinguistPayAndOtherCosts}
                        onChange={(event) => {
                          setShowLinguistPayAndOtherCosts(event.target.checked);
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ display: "block", ml: 4.5, color: theme.palette.text.secondary }}
                      >
                        Reveal expanded cost fields for pay, fees, and delivery-related charges.
                      </Typography>
                    </Box>
                  </HtmlTooltip>

                  {isNewAssignment && targetLanguages.length > 1 ? (
                    <HtmlTooltip
                      title={
                        <Box p=".5rem">
                          <Typography
                            color="inherit"
                            style={{ whiteSpace: "pre-line" }}
                          >
                            {
                              "Yes: Create one separate service for each target language. Each service will have its calculations based on a single language, with incrementing project numbers."
                            }
                            {`\n\nNo: Create one service with all target languages (current behavior).`}
                          </Typography>
                        </Box>
                      }
                    >
                      <Box sx={{ py: 0.25 }}>
                        <FormControlLabel
                          control={<Switch sx={optionSwitchSx} />}
                          id={"createServicePerLanguage"}
                          label="Create One Service Per Language"
                          checked={createServicePerLanguage}
                          disabled={targetLanguages.length <= 1}
                          onChange={(event) => {
                            setCreateServicePerLanguage(event.target.checked);
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{ display: "block", ml: 4.5, color: theme.palette.text.secondary }}
                        >
                          Split multi-language requests into separate services with incrementing project numbers.
                        </Typography>
                      </Box>
                    </HtmlTooltip>
                  ) : null}
                </Paper>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Overlay>

      {/* Paid Assignment Warning Dialog */}
      <Dialog
        open={showPaidWarning}
        onClose={handlePaidWarningCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            backgroundColor: theme.palette.background.default,
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: theme.palette.warning.main,
            color: theme.palette.warning.contrastText,
            fontWeight: "bold",
          }}
        >
          ⚠️ Paid Assignment Warning
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <DialogContentText
            sx={{
              color: theme.palette.text.primary,
              whiteSpace: "pre-line",
              fontWeight: 500,
            }}
          >
            {paidWarningMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={handlePaidWarningCancel}
            color="inherit"
            variant="outlined"
            sx={{ borderRadius: "8px" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handlePaidWarningConfirm}
            color="warning"
            variant="contained"
            sx={{ borderRadius: "8px" }}
            autoFocus
          >
            Yes, Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Lock Dialog */}
      <EditLockDialog
        open={isLocked}
        lockedBy={lockedBy}
        onClose={() => {
          releaseLock();
          setOverlayName("");
        }}
      />
    </>
  );
};

export default OtherServiceNewEdit;
